import {
  DEPARTMENTS, DESIGNATIONS,
  canManageSchedules, canBuildReports, isMasterAdmin, isDepartmentAdmin, isStaff, canViewUsers,
  canViewProperties, canManageHistoricalSales, canViewLegalReporting, canReviewNoc, canForwardLegalDocumentRequest,
  canViewCustomers, canViewSales, canViewReallocations, canViewReallocationRegistrations, canViewServiceRequests, canViewServiceCatalogue,
  canViewDocuments, canManageDocumentConfig,
  canViewOwnNotifications, canViewAuditTrail, canViewOwnActivity, canViewTasks, canAdministerTasks,
  canViewReports, canAdministerVerificationQueue, canSubmitUnitsToLegal, canReceiveLegalIntake,
  canViewVerifications,
  canFulfilDocumentRequests, canAdministerOrganisation, canViewAuditReports, canResetPassword, canMonitorOrganisation,
  canViewSalesInventory, canAdministerSalesInventory, canUseSalesWorkspace,
  canUseLegalWork, isLawyer, canActOnLegalIntake,
  canManagePropertyRequirements, canManageRegistrationRequirements, canReadLegalRequirements,
  canViewHistoricalApplications,
  canViewHistoricalCustomers,
} from "./access";
import { canSeeReusabilityEntry } from "./reusabilityGovernance";

/**
 * NAVIGATION PROFILES — the configuration the one Sidebar component renders.
 *
 * ── THE ARCHITECTURE (Administration Part 1 §4) ─────────────────────────────
 *
 *   user → designation → department → navigation profile → allowed items → sidebar
 *
 * `Sidebar.jsx` holds NO menu of its own any more: it asks `navigationProfileFor(user)` which profile
 * this actor's authority calls for, applies the access predicates, and renders. Adding a department
 * or reshaping one department's menu is an edit to this file alone — there is never a second Sidebar
 * component, and no profile is produced by filtering another one.
 *
 * ── STRUCTURE IS NOT ACCESS, AND NEITHER IS SECURITY (Part 1 §5) ────────────
 * A profile chooses WHICH destinations to offer and in what order. It never decides whether the
 * caller may use them: every row still carries the `can` predicate from `utils/access.js` that
 * mirrors its server-side gate, and `visibleSections` applies it. Nothing in this file grants
 * anything. The backend re-checks every request and remains the only security boundary — a Property
 * Admin who types a Legal URL is refused by the server, not by the absence of a menu row.
 *
 * ── ADMINISTRATION IS NOT AN OPERATING DEPARTMENT (Part 1 §1, §3, §7) ───────
 * `ADMINISTRATION_NAV` is the Master Admin's organisation-CONTROL surface. It deliberately carries
 * no Properties, Ventures, Plots, Field Verification, Legal, Sales, Bookings, Payments, Documents or
 * Customer Services row, and no department task queue and no "My Tasks": Administration owns no
 * operational work, so offering those would turn a control area into a sixth operating department.
 * Cross-department information reaches the Master Admin through Departments and Work Tracking, which
 * are monitoring surfaces — not by importing another department's workspace.
 *
 * EVERY DESTINATION BELOW IS A REGISTERED ROUTE. Nothing here invents a screen.
 */

// ── The vocabulary: every real destination, with the predicate that authorizes it ─────────────
// `end` marks a NavLink that must match exactly, so "/" and "/tasks" do not stay lit for their
// children. `resource`/`action` document which server-side gate the row answers to.
export const DEST = {
  dashboard:     { to: "/", icon: "bi-speedometer2", label: "Dashboard", end: true, resource: "dashboard", action: "read", can: isStaff },

  myTasks:       { to: "/tasks/my", icon: "bi-person-check", label: "My Tasks", resource: "tasks", action: "read:own", can: canViewTasks },
  allTasks:      { to: "/tasks", icon: "bi-list-task", label: "All Tasks", end: true, resource: "tasks", action: "read", can: canViewTasks },
  deptQueue:     { to: "/tasks/queue", icon: "bi-inbox", label: "Department Queue", resource: "tasks", action: "administer", can: canAdministerTasks },

  // ── Part 3 · THE THREE ADMIN DECISION QUEUES (§5, §6, §7) ───────────────────────────────────
  //
  // NO NEW ROUTE, NO NEW PAGE, NO NEW REQUEST MODEL. `/tasks/queue` already answers all four
  // questions through its own `?view=`, added when the queue's view state moved into the URL: the
  // page reads the same scoped task list and picks out the pending `reviews[]`,
  // `reassignmentRequests[]` and `extensionRequests[]` entries. What those views never had was an
  // ADDRESS anyone could navigate to — a reviewer had to land on the queue and change a dropdown, so
  // no sidebar row, link or badge could point at "the extension requests waiting on me".
  //
  // These three rows are that address. `isRowActive` compares the query string, so the four rows
  // sharing `/tasks/queue` highlight independently and exactly one is ever lit — the same mechanism
  // the R&S registration rows already use, reused rather than re-invented.
  //
  // ONE PREDICATE, THE EXISTING ONE. All three carry `canAdministerTasks`, which is what the queue
  // route's own `RoleRoute` enforces and what the server re-checks per record; a Department Admin is
  // scoped to their own department by the endpoint, not by this menu.
  completionReviews:  { to: "/tasks/queue?view=completion", icon: "bi-clipboard-check", label: "Completion Reviews", resource: "tasks", action: "administer", can: canAdministerTasks },
  reassignmentQueue:  { to: "/tasks/queue?view=reassignment", icon: "bi-arrow-left-right", label: "Reassignment Requests", resource: "tasks", action: "administer", can: canAdministerTasks },
  extensionQueue:     { to: "/tasks/queue?view=extension", icon: "bi-calendar-plus", label: "Extension Requests", resource: "tasks", action: "administer", can: canAdministerTasks },

  // ── D-12 · THE LEGAL ADMIN'S FORWARDING QUEUE (owner ruling B, 2026-09-04) ──────────────────
  //
  // THE FIFTH REQUESTS CHILD, and the only one that is not a view of `/tasks/queue` — because it is
  // not a task decision at all. The other four ask "what should happen to this task"; this one asks
  // "should this ask of mine become another department's work". Different record, different
  // workflow, different authority, so a real route of its own.
  //
  // IT IS NOT "DOCUMENT REQUESTS". `/properties/document-requests` is the PROPERTY department's
  // fulfilment queue — what Legal is waiting for — and is untouched. This is the LEGAL side: the
  // additional requests still Open, still waiting on the Legal Admin control point that
  // business/07 §3 (A2) created.
  //
  // GATED BY THE PREDICATE THAT MIRRORS THE SERVER'S `assertCanForward`, not by
  // `canViewLegal` — Legal membership is not the question, the forwarding decision is. So a Legal
  // EMPLOYEE and a LAWYER do not receive this row, and the endpoint refuses them regardless. C3 —
  // whether the Lawyer takes a distinct sidebar at all — is a SEPARATE unresolved decision and is
  // untouched here.
  legalDocForwarding: { to: "/legal/document-requests/awaiting-forward", icon: "bi-file-earmark-arrow-up", label: "Legal Additional Document Requests", resource: "legalDocumentRequests", action: "forward", can: canForwardLegalDocumentRequest },
  // ── PHASE 2.2 (owner-ruled 2026-09-07) · THE STANDALONE VERIFICATIONS SECTION IS GONE ──────────
  //
  // `verifications` (/verifications), `fvInProgress`, `fvAwaitingReview` and `fvCompleted` are all
  // REMOVED. The last three were `/verifications?verificationStatus=...` — filtered views of the same
  // standalone register, which is what made them a list under a section rather than destinations of
  // their own.
  //
  // WHAT REPLACED THEM, AND WHY THE ROWS WERE REDUNDANT RATHER THAN MERELY UNWANTED:
  //   • an employee's own work            -> Field Operations -> My Tasks
  //   • an admin's assignment and review  -> Field Verification Queue (kept below)
  //   • a venture's operational picture   -> Property -> Venture -> Assignment Progress, which already
  //                                          reports exactly these states per plot and per employee
  //
  // NOT REPLACED BY ANOTHER GENERIC ROW. A second "Verifications" destination under a different name
  // would be the same standalone list with a new label.
  //
  // NOTHING IS UNROUTED. `/verifications` and `/verifications/:id` still resolve, still carry their
  // guards, and are still reached from the queue, from a task and from Assignment Progress. What is
  // withdrawn is the standing invitation to browse the register as a menu destination.

  legalHandoff:  { to: "/verifications/handoff", icon: "bi-send-check", label: "Legal Handoff", resource: "legalIntake", action: "submit", can: canSubmitUnitsToLegal },
  legalIntake:   { to: "/legal/intake", icon: "bi-inboxes", label: "Legal Intake", resource: "legalIntake", action: "receive", can: canReceiveLegalIntake },
  // Part 9 — the Legal Department reporting board. Same authority as the intake queue: the row is
  // offered to the Legal Department Admin and the Master Admin and to nobody else, because the board
  // enumerates every lawyer's work and every unit's saleability.
  legalReporting:{ to: "/legal/reporting", icon: "bi-graph-up", label: "Legal Reporting", resource: "legalReporting", action: "read", can: canViewLegalReporting },
  // ── LEGAL PHASE 2 §9/§10 · THE LEGAL WORK AND RECORD DOORS ────────────────────────────────────
  // `Document Requests` is Legal's request WORKFLOW (not stored documents, §38); `Legal Reviews` and
  // `Legal Issues` are read surfaces over existing records (§26/§39). Each lists only what the server
  // lets the caller read. `Legal Reports` is the report catalogue opened on the Legal category, which
  // the server already filters to reports the caller can run (Phase 1 S6, §36).
  legalWorkRequests: { to: "/legal/document-requests", icon: "bi-file-earmark-arrow-up", label: "Document Requests", resource: "legalDocumentRequests", action: "read", can: canUseLegalWork },
  legalReviews:  { to: "/legal/reviews", icon: "bi-journal-check", label: "Legal Reviews", resource: "legalReview", action: "read", can: canUseLegalWork },
  legalIssues:   { to: "/legal/issues", icon: "bi-exclamation-diamond", label: "Legal Issues", resource: "legalIssues", action: "read", can: canUseLegalWork },
  legalReports:  { to: "/reports?category=I", icon: "bi-folder2-open", label: "Legal Reports", resource: "reports", action: "read", can: canViewReports },
  // Part 11 — the NOC review board. Same authority as the reporting board: accepting a NOC releases a
  // unit, and that decision is the Legal Department Admin's alone.
  nocReview:     { to: "/legal/noc", icon: "bi-patch-check", label: "NOC Review", resource: "legalNoc", action: "review", can: canReviewNoc },
  // LEGAL PHASE 9 §29 (business/18 A2) — NOC Requests: NOCs asked for by Registration. The Legal Admin assigns and
  // decides; a lawyer sees only the requests assigned to them (the server narrows the list), so the row is theirs too.
  nocRequests:   { to: "/legal/noc-requests", icon: "bi-patch-check", label: "NOC Requests", resource: "legalNoc", action: "read", can: canUseLegalWork },

  docRequests:   { to: "/properties/document-requests", icon: "bi-file-earmark-arrow-up", label: "Documents Requested", resource: "documents", action: "fulfil", can: canFulfilDocumentRequests },
  fvQueue:       { to: "/verifications/queue", icon: "bi-inbox-fill", label: "Field Verification Queue", resource: "verifications", action: "administer", can: canAdministerVerificationQueue },

  // ── FIELD PHASE 2 §8/§21/§22/§24 · THE FIELD DEPARTMENT'S OWN WORK VOCABULARY ─────────────────
  //
  // Four rows, and every one is an ADDRESS for a surface that already exists. No route, page or
  // endpoint is invented here, and none of these grants anything: each carries the SAME predicate
  // the destination's own `RoleRoute` already enforces and the server re-checks per record.
  //
  // `fieldQueue` IS `fvQueue` UNDER §8's NAME. §8 calls the assignment board "Field Queue" and §21
  // describes what it answers — "what Field work requires departmental attention?". Same route, same
  // predicate, same page. It is a SECOND VOCABULARY ROW for a destination `fvQueue` also names —
  // deliberate, and the device `sales`/`salesBookings` and `customers`/`salesCustomers` already use:
  // the label belongs to the department whose workspace it is.
  //
  // `fieldCompletionReviews` IS `completionReviews` RENAMED, and only for Field. §22 is explicit
  // that "Completion Reviews" collides with the PROPERTY department's Data Completion — a Field
  // Admin reading it has no way to tell whose completion is meant. The route, the `?view=completion`
  // filter and `canAdministerTasks` are all unchanged; only the word is.
  fieldQueue:            { to: "/verifications/queue", icon: "bi-inbox-fill", label: "Field Queue", resource: "verifications", action: "administer", can: canAdministerVerificationQueue },
  // FIELD STEP 2 §14 (owner, final) — the row is named "Completion Reviews", the finalized label.
  fieldCompletionReviews: { to: "/tasks/queue?view=completion", icon: "bi-clipboard-check", label: "Completion Reviews", resource: "tasks", action: "administer", can: canAdministerTasks },
  // §24/§25 — the reinspection surface. `canViewVerifications` is `isStaff`, and that is correct
  // here BECAUSE THE SERVER SCOPES THE ANSWER: `listRevisits` calls `listVerifications`, so an
  // employee sees their own reinspections and an admin sees the department's. A narrower predicate
  // would take from an operative the one screen that tells them what they have been sent back to do.
  fieldRevisits:         { to: "/verifications/revisits", icon: "bi-arrow-repeat", label: "Revisit / Reinspection", resource: "verifications", action: "read", can: canViewVerifications },

  // ── FIELD STEP 2 §14/§53/§119 · THE FIELD VERIFICATIONS RECORD REGISTER (owner, final) ─────────
  // Historical and READ-ONLY: every Field Verification the caller's scope admits, with its handoff
  // state, and a read-only view of each. It creates nothing, reviews nothing and hands nothing over —
  // those acts live on Completion Reviews and Legal Handoff. Its path is its own; the retired bare
  // `/verifications` register stays retired. The note below records why the row was withheld until
  // this ruling and is kept as history.
  // A FIELD-SIDE register: offered to the Field Operations Admin (and the Master Admin), the same grant
  // as the Field Queue. Found in the browser: other departments' admins could open it by URL.
  fieldVerificationRecords: { to: "/verifications/records", icon: "bi-folder2-open", label: "Field Verifications", resource: "verifications", action: "read", can: canAdministerVerificationQueue },
  // ── (HISTORY) NO `Field Verifications` RECORD ROW — AND WHY NOT (Phase 2 §8/§49 vs §12/§85-J) ─
  //
  // §8's target structure lists `Records ▾ → Field Verifications`, and §49 describes it as a
  // read-only record surface that must never become a second creation workflow. But §12 is headed
  // "LEGACY VERIFICATION — HARD REQUIREMENT" and forbids reintroducing the "Verifications register",
  // and §85(J) requires the old standalone workflow to remain inaccessible.
  //
  // THE REPOSITORY ALREADY ANSWERED THIS, and the answer is pinned. `P4-E2` asserts, of the Field
  // Operations workspace, that "the removed register lights nothing, BECAUSE NO ROW POINTS AT IT" —
  // an owner-approved Phase 2.2 ruling that `/verifications` is not a navigation destination for this
  // department. A row here makes that assertion fail, and re-pinning a governance assertion to admit
  // the very surface it was written to exclude is not a re-pin, it is a reversal.
  //
  // So the row is NOT added, the conflict is reported for a ruling rather than resolved by guessing
  // (§84), and the recommendation is recorded in the Phase 2 delivery. Reversing this later is one
  // destination and one profile line — nothing else in the phase depends on it.
  notifications: { to: "/notifications", icon: "bi-bell", label: "Notifications", resource: "notifications", action: "read:own", can: canViewOwnNotifications },
  myActivity:    { to: "/audit/my-activity", icon: "bi-person-lines-fill", label: "My Activity", resource: "audit", action: "read:own", can: canViewOwnActivity },

  properties:    { to: "/properties", icon: "bi-buildings", label: "Properties", resource: "properties", action: "read", can: canViewProperties },
  // CUSTOMER SUPPORT PHASE 2 (2026-09-30; approved reconciliation §F) — the Properties Department's register
  // of assets sold before this system existed. Offered ONLY in the two Properties profiles below; the
  // predicate is the server's own gate (Properties department, Master Admin).
  historicalSales: { to: "/historical-sales", icon: "bi-clock-history", label: "Historical Sales", resource: "historicalSales", action: "read", can: canManageHistoricalSales },
  // LEGACY GLOBAL VENTURES / PLOTS ROWS — REMOVED. `/ventures` and `/plots` were the company-wide
  // registers; both are now retired routes. No named profile referenced these rows, but they
  // survived in `ASSETS` and in `employeeProfile`'s default, which is how the FALLBACK profiles
  // still carried them: an employee in a department this file does not name resolved to
  // `GENERIC_EMPLOYEE_NAV` and was offered both registers. Leaving them would now offer that actor
  // two rows that lead to Not Found, which is the dead navigation door the navigation rules forbid.
  // A venture is reached from its property, and a venture's plots from the venture.

// ══════════════════════════════════════════════════════════════════════════════════════════════
// PHASE 3.1 (owner-ruled 2026-09-08) · THE STANDALONE LEGAL CASE WORKFLOW IS GONE
// ══════════════════════════════════════════════════════════════════════════════════════════════
//
// `legal` (/legal — the Case register) and `myCases` (/legal/my) are REMOVED from every profile,
// together with `/legal/new` ("Add New Case"). The old Case work-management workflow is obsolete:
// Legal work is managed through Legal Intake -> Legal Task -> Parent Assessment -> Plot Legal
// Review -> NOC, and a Case is no longer how a lawyer organises their work.
//
// ── THIS SUPERSEDES PART 4 §30/§31 (SIDEBAR-A05, SIDEBAR-A22) ─────────────────────────────────
// Those rulings ADDED `My Cases` for the Lawyer/Legal Employee as "the door the audit recorded as
// missing", and moved `Legal` under its own heading rather than deleting it. Both were correct
// when a Case WAS the lawyer's work record. Phase 3.1's ruling retires that premise, so the rows
// go with it. The reasoning is recorded rather than deleted: the door §30 opened is now
// `My Tasks`, which is the unit of assigned legal work and is unchanged.
//
// ── WHAT IS NOT REMOVED, AND WHY THE DISTINCTION MATTERS ─────────────────────────────────────
// The `LegalCase` MODEL, its block state, and `getEffectiveBlock()` are untouched. LegalCase is
// still the authoritative sales-block record, and `getEffectiveBlock` is still its single reader
// — consumed by Module 8's sale eligibility and by the Legal Review clearance signal. What is
// withdrawn is the standing invitation to BROWSE and CREATE cases as a way of doing legal work.
//
// An existing block therefore stays releasable: `/legal/:id` survives as a narrow BLOCK RECORD
// view (`LegalBlockRecord`), not as the old case-management workspace. Phase 3.1 deliberately does
// NOT migrate the block writer — that is a later, separately verified step.

  // Legal keeps the frozen PD11 restricted read for other departments; the three below resolve a
  // department read tier server-side and refuse Administration staff who are not the Master Admin.
  customers:     { to: "/customers", icon: "bi-person-vcard", label: "Customers", resource: "customers", action: "read", can: canViewCustomers },
  // ── MODULE 8's BOOKINGS LIST MOVED FROM "/sales" TO "/sales/bookings" ────────────────────────
  // Registration & Sales Part 1 §6 gives "/sales" to the Sales Dashboard, so the frozen Module 8
  // bookings board took the child path that names what it actually is. NOTHING WAS REMOVED: the
  // list, the booking detail (`/sales/:id`), payments and every Module 8 route are unchanged and
  // still reachable, and no backend route moved at all. What changed is which screen this row leads
  // to — and it now leads to the one the label has always promised.
  sales:         { to: "/sales/bookings", icon: "bi-journal-text", label: "Sales & Bookings", resource: "sales", action: "read", can: canViewSales },

  // ── Registration & Sales Part 1 · the department's own destinations (§5) ─────────────────────
  // §5 asks for a FOCUSED sales sidebar and names the eight rows below. It also names what must NOT
  // appear — Legal, Property Department, Vendors, Data Completion, Lawyer Tasks, KYC, Customers,
  // Payments, Registration, Refunds, Cancellation — and adds the rule that broader permissions are
  // not a reason to expose another department's workspace. `REGISTRATION_SALES_NAV` below carries
  // these rows and nothing else.
  //
  // Every one of them is a REGISTERED ROUTE with its own `RoleRoute` guard; nothing here invents a
  // screen, and nothing here grants anything.
  salesDashboard:   { to: "/sales", icon: "bi-speedometer2", label: "Dashboard", end: true, resource: "salesInventory", action: "read", can: canViewSalesInventory },
  salesTasks:       { to: "/sales/tasks", icon: "bi-list-task", label: "Sales Tasks", resource: "salesInventory", action: "read", can: canViewSalesInventory },
  salesReassignments: { to: "/sales/reassignment-requests", icon: "bi-arrow-left-right", label: "Reassignment Requests", resource: "salesInventory", action: "read", can: canAdministerSalesInventory },
  salesEmployees:   { to: "/sales/employees", icon: "bi-people", label: "Employees", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  salesHistory:     { to: "/sales/history", icon: "bi-clock-history", label: "History", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  // Legacy Cleanup Phase 1B (2026-10-11): `salesBookingConfig` removed — no profile used it; `salesAdvanceConfig` is the live row for the same route.
  // Legacy Cleanup Phase 1B (2026-10-11): the Part 5 `salesRegistrations` / `salesApprovals` rows were removed — no profile used them; the legacy queues
  // stay reachable from the R&S dashboard's "Earlier purchases" block.

  // Legacy Cleanup Phase 1B (2026-10-11): the Part 6 §30 `salesCustomers` / `salesBookings` / `salesPayments` rows were removed — no profile used them
  // (the shared `customers` and `sales` rows, and the sale workspace's payments, remain).
  // ── R&S PART 1 (2026-09-19, business/09 A1) · the WORK AREAS of a task-first department ─────────
  // Allocation is by Property / Venture (never Plot); Available Work is DERIVED availability, grouped by
  // the business hierarchy. Payment Verification is the same payments board, named for the job the
  // admin does there, until Part 2 moves payments into the transaction.
  salesAvailableWork: { to: "/sales/work/available", icon: "bi-grid-3x3-gap", label: "Available Work", resource: "salesWorkspace", action: "read", can: canUseSalesWorkspace },
  salesAllocation:    { to: "/sales/allocation", icon: "bi-diagram-3", label: "Allocation", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  // R&S PART 2 (2026-09-19, MDS RS2) — payments now live inside the Sale Transaction, so Payment
  // Verification opens the admin's review queue (proof preview, verify, return). Financial Completion and
  // Commercial Changes are the other two decisions that are the admin's alone; Transactions is the
  // supervisory register. The Module 8 payments list keeps its route for payments recorded before Part 2.
  salesPaymentVerification: { to: "/sales/reviews?tab=payments", icon: "bi-cash-coin", label: "Payment Verification", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  salesFinancialCompletion: { to: "/sales/reviews?tab=financial", icon: "bi-patch-check", label: "Financial Completion", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  salesCommercialChanges: { to: "/sales/reviews?tab=commercial", icon: "bi-currency-exchange", label: "Commercial Changes", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  salesTransactions: { to: "/sales/transactions", icon: "bi-receipt", label: "Transactions", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  // R&S PART 3 (2026-09-20, MDS RS3) — the ACTUAL registrations of Part 2 sales: grouping, documents, NOC,
  // readiness and the completion the admin approves. The three legacy Registration rows below keep serving
  // the per-plot registrations created before Part 2.
  salesRegistrationWork: { to: "/sales/work/registrations", icon: "bi-journal-check", label: "Registration", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },

  // Legacy Cleanup Phase 1B (2026-10-11): the three legacy registration-queue rows (`salesRegReady` / `salesRegTasks` / `salesRegCompleted`) were removed —
  // no profile used them.
  // PHASE 12 FINAL CORRECTION §20 — NOC belongs to the REGISTRATION workflow, not to a global configuration screen.
  // This department's own surface (its own registrations' NOC requests), so §5's "no other department" rule holds.
  salesNocRequests:  { to: "/sales/noc-requests", icon: "bi-patch-check", label: "NOC Requests", resource: "salesRegistration", action: "read", can: canViewSalesInventory },

  // ── Registration & Sales Part 2 · the Sales EMPLOYEE's personal work queue (§4) ───────────
  // §4: "The employee UI must feel like a personal work queue, not an administration portal." Six
  // rows, and every one of them is about the employee's OWN work. Each is a registered route with its
  // own guard; none leads to an admin board.
  empDashboard:  { to: "/sales/employee", icon: "bi-speedometer2", label: "Dashboard", end: true, resource: "salesWorkspace", action: "read:own", can: canUseSalesWorkspace },
  empTasks:      { to: "/sales/employee/tasks", icon: "bi-list-task", label: "My Tasks", resource: "salesWorkspace", action: "read:own", can: canUseSalesWorkspace },
  empPlots:      { to: "/sales/employee/plots", icon: "bi-grid-3x3", label: "My Plots", resource: "salesWorkspace", action: "read:own", can: canUseSalesWorkspace },
  // Legacy Cleanup Phase 1B (2026-10-11): `empCustomers` / `empRequests` were removed — no profile used them; the live
  // rows for the same routes are `empMyCustomers` / `empMyRequests`.
  // PART 9 (2026-10-08; frozen Part 9 spec §57) — the SAME entry, now opening the current workflow's list (earlier records are
  // one click away from it). No menu entry is added or moved; the wider navigation review comes after Part 10.
  reallocations: { to: "/reallocations-v2", icon: "bi-arrow-left-right", label: "Reallocations", resource: "reallocations", action: "read", can: canViewReallocations },
  // PART 9 FINAL (2026-10-11; owner spec §3.2; MDS CS6 §50.7) — Reallocation Registration, its own destination (not a tab).
  reallocationRegistrations: { to: "/reallocation-registrations", icon: "bi-journal-check", label: "Reallocation Registration", resource: "salesRegistration", action: "read", can: canViewReallocationRegistrations },
  services:      { to: "/service-requests", icon: "bi-headset", label: "Customer Services", resource: "serviceRequests", action: "read", can: canViewServiceRequests },

  // ── Part 2 §34 · THE CUSTOMER SUPPORT ADMIN'S OPERATIONAL QUEUES ─────────────────────────────
  //
  // NO NEW ROUTE, NO NEW PAGE, NO NEW ENDPOINT, NO NEW FIELD. Every row below is an ADDRESS for a
  // view the register already answers — the same device `/tasks/queue?view=…` and
  // `/sales/registrations?view=…` already use, and the reason `isRowActive` compares the query
  // string. What these rows add is the ability to POINT at "the requests waiting on triage", which
  // previously required landing on the register and changing a dropdown.
  //
  // ── EVERY FILTER VALUE IS A FROZEN ENUM MEMBER, NOT AN INTERPRETATION ──────
  // `New`, `Escalated` and `Resolved` are three of the eleven values in
  // `constants/serviceRequestEnums.js`, and `buildListQuery` validates the parameter against that
  // same list server-side. "Awaiting Closure" is `Resolved` because the frozen transition map gives
  // `Resolved → Closed`; it is the state a request sits in while it waits to be closed, not a new
  // status and not a computed bucket.
  //
  // ── SLA & OVERDUE IS AN EXISTING ROUTE, DELIBERATELY REUSED ────────────────
  // `/service-requests/sla-due` is a REGISTERED route (`AppRoutes.jsx`) with its own page
  // (`SlaDuePanel.jsx`) and its own backend read, ordered by due date. Pointing this row at a
  // hand-rolled `?slaState=Breached` view of the register instead would have invented a second,
  // thinner answer to a question the application already answers properly.
  //
  // ── THE GATE IS THE ONE THE ROUTES THEMSELVES CARRY ───────────────────────
  // `canViewServiceRequests` — the same predicate `/service-requests` and `/service-requests/sla-due`
  // are guarded by in `AppRoutes.jsx`, which in turn mirrors `resolveReadTier`. These rows are
  // STRUCTURALLY absent from the employee profile (they are declared only in `CUSTOMER_SUPPORT_NAV`),
  // so a Customer Support employee is not handed an oversight surface — and, as everywhere, the
  // server re-checks the caller regardless of which menu offered the door.
  // ── Part 3 §14/§19 · THE CUSTOMER SUPPORT EMPLOYEE'S WORK DOOR ──────────────────────────────
  //
  // `assignedEmployee=me` — and `me` is resolved by the SERVER from the session
  // (`serviceRequestService.listServiceRequests`), never from anything the browser supplies. The
  // same URL therefore means a different person for every reader, which is why one row can serve
  // every employee and why no id appears in the menu.
  //
  // IT IS A VIEW, NOT A NEW ENTITY (§14). Same route, same page, same ServiceRequest records as the
  // register — the query narrows it. No `/my-service-requests` route was invented.
  //
  // THE PARAMETER IS NOT THE SECURITY. Since the Part 3 ruling a Customer Support employee holds the
  // EMPLOYEE read tier, so the server returns their own assigned work whether or not this parameter
  // is present, and deleting or editing it in the address bar changes nothing. The row exists to
  // NAME the worklist, not to enforce it.
  myRequests:        { to: "/service-requests?assignedEmployee=me", icon: "bi-person-rolodex", label: "My Requests", resource: "serviceRequests", action: "read:own", can: canViewServiceRequests },

  // ── Part 4 §20 · THE FIELD OPERATIONS VERIFICATION BOARD, BY STATE ───────────────────────────
  //
  // §20 asks the Field Operations admin for "Pending Assignment / In Progress / Awaiting Review /
  // Completed". All four are views of surfaces that ALREADY EXIST — `/verifications/queue` is the
  // assignment board, and `/verifications` accepts a `verificationStatus` filter the service applies
  // server-side (`verificationService`). NO ROUTE, PAGE OR ENDPOINT WAS CREATED, and the values are
  // the frozen `VERIFICATION_STATUS` members, not labels invented here.
  //
  // "Awaiting Review" is `Submitted`, because that is precisely the state the employee has handed the
  // report in and the ADMIN owes the next act — the same reading `legalIntakeService.ineligibilityReason`
  // already puts in words ("the outstanding action is the reader's own review").
  //
  // THE FILTER IS NOT THE AUTHORIZATION. `/verifications` scopes to the caller server-side; these
  // rows narrow that scope, they never widen it.
  // ── THE SPACE IS PERCENT-ENCODED, AND THAT IS LOAD-BEARING ──────────────────────────────────
  // `In Progress` is a frozen enum value containing a space. A browser NORMALISES the address bar to
  // `?verificationStatus=In%20Progress`, so `isRowActive` — which compares `row.to`'s query against
  // `location.search` verbatim — never matched a row declaring the raw space, and the parent
  // "Verifications" row lit instead of this one. Browser-found, not predicted; the two rows without
  // a space were unaffected, which is exactly why a literal is the wrong thing to trust here.

  // ── Part 4 §30 · THE LAWYER'S OWN CASE PORTFOLIO (SIDEBAR-A05) ───────────────────────────────
  //
  // `/legal/my` is a REGISTERED ROUTE that has existed all along with no door: `LegalCasesList`
  // serves both `/legal` and `/legal/my`, and on the latter it scopes to `assignedLawyer = me`. The
  // PHASE 3.1 — `myCases` REMOVED with the Case workflow it belonged to. Part 4 §30 added it as
  // "the door the audit recorded as missing" for the Lawyer, and §31 argued a case is not a task.
  // Both were true while a Case was the lawyer's work record; the ruling retires that premise. The
  // lawyer's work door is now `My Tasks`, which is unchanged.

  // ── Part 4 §39/§45/§48 · CUSTOMER SUPPORT'S OWN REFERENCE SURFACES ───────────────────────────
  //
  // `csAllRequests` is the register (`/service-requests`) under the name §39 gives it. It is a
  // SECOND VOCABULARY ROW for a route `D.services` also names — deliberate, and the same device
  // `sales`/`salesBookings` and `customers`/`salesCustomers` already use: the label belongs to the
  // department whose workspace it is, and `D.services` stays "Customer Services" for the departments
  // that merely reference it.
  //
  // "ALL REQUESTS" MEANS ALL THE CALLER MAY SEE, NOT ALL THAT EXIST (§46). Since Part 3 a Customer
  // Support EMPLOYEE holds the assigned-only read tier, so for them this row and My Requests return
  // the same rows — the label is not a permission and does not widen one. The server decides.
  csAllRequests:     { to: "/service-requests", icon: "bi-headset", label: "All Requests", resource: "serviceRequests", action: "read", can: canViewServiceRequests },
  serviceCatalogue:  { to: "/service-requests/catalogue", icon: "bi-journal-bookmark", label: "Service Catalogue", resource: "serviceCatalogue", action: "read", can: canViewServiceCatalogue },

  csNewRequests:     { to: "/service-requests?status=New", icon: "bi-envelope-plus", label: "New Requests", resource: "serviceRequests", action: "read", can: canViewServiceRequests },
  csSlaDue:          { to: "/service-requests/sla-due", icon: "bi-alarm", label: "SLA & Overdue", resource: "serviceRequests", action: "read", can: canViewServiceRequests },
  csEscalations:     { to: "/service-requests?status=Escalated", icon: "bi-exclamation-triangle", label: "Escalations", resource: "serviceRequests", action: "read", can: canViewServiceRequests },
  csAwaitingClosure: { to: "/service-requests?status=Resolved", icon: "bi-check2-circle", label: "Awaiting Closure", resource: "serviceRequests", action: "read", can: canViewServiceRequests },
  // CUSTOMER SUPPORT PHASE 6 (owner ruling R8; MDS CS6 §17.3, §17.4, §35.12) — the historical customer applications
  // queue, under "Customer Onboarding". The Master Admin reaches the same page by its address (read-only, R10).
  csApplications:    { to: "/historical-applications", icon: "bi-person-vcard", label: "Applications", resource: "historicalApplications", action: "read", can: canViewHistoricalApplications },
  // CUSTOMER SUPPORT PHASE 7 (P7-R18; U1.4 / CL-U1.4: the CS Admin only; U1.5 / S-5: the Master Admin, read-only).
  csHistoricalCustomers: { to: "/historical-customers", icon: "bi-people-fill", label: "Historical Customers", resource: "historicalCustomers", action: "read", can: canViewHistoricalCustomers },

  documents:     { to: "/documents", icon: "bi-file-earmark-text", label: "Documents", resource: "documents", action: "read", can: canViewDocuments },
  audit:         { to: "/audit", icon: "bi-journal-text", label: "Audit & Timeline", resource: "audit", action: "read", can: canViewAuditTrail },
  users:         { to: "/users", icon: "bi-people", label: "Users & Employees", resource: "users", action: "read", can: canViewUsers },

  catalogue:     { to: "/reports", icon: "bi-folder2-open", label: "Report Catalogue", resource: "reports", action: "read", can: canViewReports },
  analytics:     { to: "/analytics", icon: "bi-bar-chart-line", label: "Analytics", resource: "reports", action: "read", can: canViewReports },
  builder:       { to: "/builder", icon: "bi-tools", label: "Report Builder", resource: "reports", action: "build", can: canBuildReports },
  saved:         { to: "/saved", icon: "bi-bookmark-star", label: "Saved Reports", resource: "reports", action: "build", can: canBuildReports },
  exports:       { to: "/exports", icon: "bi-download", label: "Export Centre", resource: "exports", action: "read:own", can: isStaff },
  schedules:     { to: "/schedules", icon: "bi-calendar-week", label: "Scheduled Reports", resource: "reports", action: "schedule", can: canManageSchedules },

  // ── Administration Part 1 · the Master Admin's organisation-control destinations ────────────
  // `overview`, `employees`, `taskMonitoring`, `reports`, `auditActivity`, `usersAccess` and
  // `departmentAdmins` are EXISTING routes re-labelled for the control area — no route is duplicated
  // and no deep link is broken. `departments`, `configuration` and the three Work Tracking boards are
  // the only new paths, and each is registered in AppRoutes with its own RoleRoute guard.
  overview:      { to: "/", icon: "bi-speedometer2", label: "Overview", end: true, resource: "dashboard", action: "read", can: isStaff },
  departments:   { to: "/administration/departments", icon: "bi-diagram-2", label: "Departments", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  // ── EMPLOYEES IS NOT USERS & ACCESS (owner correction, 2026-08-22) ────────────────────────────
  // These two rows were briefly the same destination — `/users` under two labels and two filters —
  // on the reasoning that reusing a route beats duplicating a screen. They are two different
  // QUESTIONS and now have two destinations:
  //
  //   Employees      → organisation-wide WORKFORCE intelligence. Read-only, staff only. Department,
  //                    designation, status and headcount; workload and performance named for Part 2.
  //   Users & Access → ACCOUNT and ACCESS administration. Read-write, staff AND customer logins:
  //                    create, edit, change designation/department, change status, reset password.
  //
  // Different purpose, audience, permission and destination — but ONE data source. Both read the
  // frozen `GET /users`; no second user model and no duplicated backend was created.
  employees:     { to: "/administration/employees", icon: "bi-people", label: "Employees", resource: "users", action: "read", can: canAdministerOrganisation },

  departmentPerformance: { to: "/administration/work-tracking/departments", icon: "bi-bar-chart-steps", label: "Department Performance", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  employeePerformance:   { to: "/administration/work-tracking/employees", icon: "bi-person-badge", label: "Employee Performance", resource: "administration", action: "monitor", can: canAdministerOrganisation },

  // ── Administration Part 4 · the four new destinations ────────────────────────────────────────
  // §1's final sidebar is intentionally SMALL. It is not, however, a licence to delete capabilities:
  // the owner's Part 4 §2 ruling is explicit that Part 1's reporting and system surfaces must be
  // preserved. So the sidebar carries ten rows, and the capabilities that no longer have a row of
  // their own are hosted on the pages they belong to. Nothing became unreachable.
  performance:   { to: "/administration/performance", icon: "bi-bar-chart-steps", label: "Performance", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  activityCentre:{ to: "/administration/activity", icon: "bi-activity", label: "Activity", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  // §12 — the ONE Administration capability that changes anything. Gated by the predicate that
  // mirrors frozen Module 1's own composed gate, NOT by `canAdministerOrganisation`: the authority to
  // reset a credential is Module 1's to define, and this row must not silently widen it.
  passwordReset: { to: "/administration/password-reset", icon: "bi-key", label: "Password Reset", resource: "users", action: "reset-password", can: canResetPassword },
  auditTrail:    { to: "/audit", icon: "bi-journal-text", label: "Audit Trail", resource: "audit", action: "read", can: canViewAuditTrail },
  // Organisation-wide task MONITORING. Part 1 pointed this at the existing all-tasks list because no
  // monitoring board existed yet; Part 3 built one, so the row now leads to it. It is a MONITORING
  // surface, not an Administration queue: Administration owns no tasks, and the board offers no
  // action on any of them. `/tasks` itself is untouched and still reachable — every operating
  // department navigates to it exactly as before.
  taskMonitoring:        { to: "/administration/work-tracking/tasks", icon: "bi-list-task", label: "Task Monitoring", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  // Part 4 §2 — the Attention Centre. The Part 2 board keeps its own route and stays reachable;
  // this row leads to the richer surface.
  bottlenecks:           { to: "/administration/bottlenecks", icon: "bi-exclamation-diamond", label: "Bottlenecks", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  // PART 7 PHASE 6 · OWNER RULING 21 — the dedicated cross-department exception view. It sits beside
  // Bottlenecks under "Work intelligence" because it answers the neighbouring question: Bottlenecks
  // is what is going SLOWLY, this is what formal exception is OPEN and whose decision it is. Gated by
  // the same `canAdministerOrganisation` (Master Admin) the server enforces — no new predicate.
  exceptions:            { to: "/administration/exceptions", icon: "bi-shield-exclamation", label: "Exceptions", resource: "administration", action: "monitor", can: canAdministerOrganisation },
  // PART 6b · BD-03 (LOCKED) — the Master Admin Decision / Action Centre. The company ruled the
  // question Parts 1-6a kept refusing to answer for themselves, and this is the row it named.
  //
  // IT SITS BESIDE EXCEPTIONS, AND THE PAIR DOES NOT OVERLAP. Exceptions answers "what formal
  // exception is open ACROSS SIX DEPARTMENTS, and whose decision is it" — most rows belong to
  // somebody else. Decisions answers the narrower question "what is waiting on ME": three queues,
  // every one of them the Master Admin’s alone by an already-contracted rule (PD12, PD10, PD18).
  //
  // Gated by `canAdministerOrganisation` — the same Master-Admin predicate the server enforces
  // through `organisationGate` and `assertCanAdministerExceptions`. No new predicate was invented,
  // and this row is not the security boundary: hiding it would not stop a typed URL, and the two
  // server-side gates are what actually refuse one.
  decisions:             { to: "/administration/decisions", icon: "bi-check2-square", label: "Decisions", resource: "administration", action: "monitor", can: canAdministerOrganisation },

  // ── REPORTS IS A SECTION, NOT A SINGLE ROW (owner correction, 2026-08-22) ─────────────────────
  // Part 1 first offered one "Reports" row and parked the rest of the reporting suite on the
  // Configuration page so nothing was stranded. That was the wrong home: these are ORGANISATION-LEVEL
  // REPORTING CAPABILITIES, not platform configuration, and Configuration is reserved for actual
  // system configuration. They are now children of Reports, which is where they belong.
  //
  // ONLY `auditReports` IS DECLARED HERE. The other six rows of that section are the SAME `catalogue`
  // / `analytics` / `builder` / `saved` / `exports` / `schedules` destinations every other profile
  // already offers — reused, not restated, so a label or predicate can never diverge between the
  // Administration sidebar and a department's. No reporting route or functionality changed at all;
  // this is an information-architecture correction and nothing else.
  auditReports:  { to: "/reports?category=O", icon: "bi-shield-check", label: "Audit Reports", resource: "reports", action: "read:audit", can: canViewAuditReports },

  auditActivity: { to: "/audit", icon: "bi-journal-text", label: "Audit & Activity", resource: "audit", action: "read", can: canViewAuditTrail },

  usersAccess:      { to: "/users", icon: "bi-shield-lock", label: "Users & Access", resource: "users", action: "read", can: canViewUsers },
  departmentAdmins: { to: "/users?designation=Department%20Admin", icon: "bi-person-gear", label: "Department Admins", resource: "users", action: "read", can: canViewUsers },
  configuration:    { to: "/administration/system/configuration", icon: "bi-sliders", label: "Configuration", resource: "administration", action: "configure", can: canAdministerOrganisation },
  // PROPERTY PART 1 §45/§47 — the Master Admin's direct door to document REQUIREMENTS.
  //
  // IT ROUTES TO THE CANONICAL PAGE, `/documents/settings`. No second route is created: §45 is
  // explicit that a user-facing label may say "Document Requirements" while the destination remains
  // the existing configuration page, and §71 forbids `/requirements`, `/document-requirements` and
  // `/requirement-history` as duplicates.
  //
  // THE LABEL IS "Document Requirements", NOT "Documents" (§47). Calling it "Documents" would put it
  // one word away from the operational document STORAGE that later parts build on the entity pages,
  // and those are different things owned by different people: Master Admin says what must exist,
  // the Property department files what actually does.
  //
  // Gated on `canManageDocumentConfig` (Master Admin) rather than `canViewDocuments` (any staff),
  // because this row is an administrative door. Read access to the page itself is unchanged.
  // ── A6 (owner-approved 2026-10-14) · READ-ONLY MONITORING rows for the Administration Department Admin ────────────
  // The SAME routes and pages as the Master Admin's rows above, under `canMonitorOrganisation`. Separate rows (not the
  // Master Admin's) so the Master Admin's sidebar is byte-for-byte unchanged; the Overview points at its A6 path
  // because "/" is this identity's own Administration home.
  monOverview:     { to: "/administration/overview", icon: "bi-speedometer2", label: "Overview", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monDepartments:  { to: "/administration/departments", icon: "bi-diagram-2", label: "Departments", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monEmployees:    { to: "/administration/employees", icon: "bi-people", label: "Employees", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monTasks:        { to: "/administration/work-tracking/tasks", icon: "bi-list-task", label: "Task Monitoring", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monBottlenecks:  { to: "/administration/bottlenecks", icon: "bi-exclamation-diamond", label: "Bottlenecks", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monDeptBoard:    { to: "/administration/work-tracking/departments", icon: "bi-bar-chart-steps", label: "Department Performance", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monEmpBoard:     { to: "/administration/work-tracking/employees", icon: "bi-person-badge", label: "Employee Performance", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monPerformance:  { to: "/administration/performance", icon: "bi-graph-up", label: "Performance", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  monActivity:     { to: "/administration/activity", icon: "bi-activity", label: "Activity", resource: "administration", action: "monitor", can: canMonitorOrganisation },
  documentRequirements: { to: "/documents/settings", icon: "bi-clipboard-check", label: "Document Requirements", resource: "documents", action: "configure", can: canManageDocumentConfig },

  // ── Administration Part 2 · three ALIASES, zero new routes ───────────────────────────────────
  // Part 2 §4's information architecture names three destinations that already exist under a
  // different label in a shared row set. An alias is used rather than a rename because `catalogue`,
  // `schedules` and `users` are shared with FIVE other department profiles — editing their label in
  // place would silently rewrite every one of those sidebars, which §36/§77 forbid. Each alias points
  // at the SAME registered route and carries the SAME predicate as the row it mirrors, so a label can
  // differ between areas but authority never can.
  //
  // (`analytics`, `builder`, `saved` and `exports` needed no alias — Part 2 §4's label for each is
  // already the shared row's own label, so those are reused directly.)
  adminReports:   { to: "/reports", icon: "bi-folder2-open", label: "Reports", resource: "reports", action: "read", can: canViewReports },
  adminSchedules: { to: "/schedules", icon: "bi-calendar-week", label: "Schedules", resource: "reports", action: "schedule", can: canManageSchedules },
  // §22's "Users". Same route and same frozen `GET /users` gate as `usersAccess`; what differs is the
  // label, because Part 2 puts the word "Users & Access" on the GROUP and a row repeating its own
  // heading tells the reader nothing.
  accessUsers:    { to: "/users", icon: "bi-people-fill", label: "Users", resource: "users", action: "read", can: canViewUsers },

  // ── P5 (MDS P5, owner 2026-09-21) · doors onto screens that already existed ───────────────────
  // Each is an EXISTING route (or, for the three requirement experiences, a department door onto the one
  // requirement engine) carrying the same predicate as its route guard (rule R7).
  salesDeptTasks:        { to: "/sales/tasks", icon: "bi-list-check", label: "Department Tasks", resource: "salesInventory", action: "read", can: canViewSalesInventory },
  salesDiscounts:        { to: "/sales/reviews?tab=terms", icon: "bi-percent", label: "Discounts", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  salesPaymentExtensions:{ to: "/sales/reviews?tab=extensions", icon: "bi-calendar-plus", label: "Payment Extensions", resource: "salesInventory", action: "administer", can: canAdministerSalesInventory },
  salesAdvanceConfig:    { to: "/sales/booking-configuration", icon: "bi-sliders", label: "Advance Configuration", resource: "salesInventory", action: "read", can: canAdministerSalesInventory },
  empMyCustomers:        { to: "/sales/employee/customers", icon: "bi-person-vcard", label: "My Customers", resource: "salesWorkspace", action: "read:own", can: canUseSalesWorkspace },
  empMyRequests:         { to: "/sales/employee/requests", icon: "bi-inbox", label: "My Requests", resource: "salesWorkspace", action: "read:own", can: canUseSalesWorkspace },
  legalDashboard:        { to: "/legal/reporting", icon: "bi-speedometer", label: "Legal Reporting", resource: "legal", action: "report", can: canViewLegalReporting },
  propertyRequirements:  { to: "/properties/requirements", icon: "bi-clipboard-check", label: "Requirements", resource: "documents", action: "configure:department", can: canManagePropertyRequirements },
  // CS-5B (owner-approved 2026-09-28) — Document Reusability: the ONE staff surface for a document type's reusability
  // (catalogue[].reusable). The R&S Department Admin always; another Department Admin only when their department
  // governs at least one type (`governing` comes from the catalogue read in Sidebar); never the Administration Admin.
  // The Master Admin reaches it from System → Configuration → Platform, not from a sidebar row.
  documentReusability: { to: "/documents/catalogue/reusability", icon: "bi-arrow-repeat", label: "Document Reusability", resource: "documents", action: "configure:reusability", can: (user, context) => canSeeReusabilityEntry(user, context && context.governingDepartments) },
  registrationRequirements: { to: "/sales/requirements", icon: "bi-clipboard-check", label: "Registration Requirements", resource: "documents", action: "configure:department", can: canManageRegistrationRequirements },
  legalRequirements:     { to: "/legal/requirements", icon: "bi-clipboard-check", label: "Requirements", resource: "documents", action: "read:department", can: canReadLegalRequirements },
};

const D = DEST;

// Shared row sets, so a department profile states what is DIFFERENT about it rather than restating
// twenty rows. The predicates still decide which of them survive for a given actor.
// PHASE 2.2 — `D.verifications` removed with the standalone register. This array feeds only
// `GENERIC_DEPARTMENT_NAV`, the fallback for a department this file does not name.
const ASSETS = [D.properties];
// PHASE 3.1 — `D.legal` removed with the Case register.
const BUSINESS = [D.customers, D.sales, D.reallocations, D.services];
const RECORDS = [D.documents, D.audit, D.users];
const REPORTING = [D.catalogue, D.analytics, D.builder, D.saved];

// ── Part 3 §2 · THE REQUESTS GROUP ───────────────────────────────────────────────────────────
//
// ONE declaration, shared by both composers, so the operational decision queues cannot drift apart
// between a Department Admin and an employee — and so "what counts as a Request" is answered once.
//
// IT IS A GROUP, NOT A DESTINATION. `Requests` goes nowhere; it collapses, using the ONE Part 1
// mechanism (`collapsible: true`). No second accordion, no per-department navigation renderer.
//
// WHAT EACH ACTOR SEES IS DECIDED BY THE PREDICATES, NOT BY THIS LIST. `visibleSections` drops the
// rows an actor is refused and then drops the whole group if nothing survived, so:
//   Department Admin / Master Admin → all four rows
//   Employee                        → My Tasks alone (`canAdministerTasks` refuses the other three)
// That is §14's requirement satisfied by the existing resolver rather than by a role test written
// here — there is no `if (role === ...)` anywhere in this file, and Part 3 adds none.
//
// ── D-12 RESOLVED — OWNER RULING B (2026-09-04) ────────────────────────────────────────────────
// The fifth child is `Legal Additional Document Requests`. It carries its own predicate, so the
// group is genuinely per-actor rather than per-department:
//   Legal Department Admin  → all five
//   other Department Admins → four (the Legal queue is refused)
//   Master Admin            → not applicable: takes ADMINISTRATION_NAV, which has no Requests group
//   Employee / Lawyer       → My Tasks alone
// Nobody is handed a row they cannot use, and no `if (role === …)` was written to achieve it.
// ── PART 4 §11/§20/§27/§39 · "MY WORK" SPLIT OUT OF "REQUESTS" ──────────────────────────────
//
// `My Tasks` used to sit INSIDE the Requests accordion beside the three administer-gated decision
// queues. That is audit finding SIDEBAR-A24, and it is a genuine category error: My Tasks is
// `read:own` — the work assigned to ME — while its three neighbours are `administer` queues about
// OTHER people's work. One row answered "what must I do today"; the other three answered "what must
// I decide about someone else". Filing them together meant an employee's only work door was buried
// in a group whose name described none of it, and an admin had to open a group called Requests to
// find their own tasks.
//
// Part 4's target structure draws them apart for EVERY department (§11, §20, §27, §39, §45), and
// this is that split: `MY_WORK` is the personal door, `REQUESTS` is the decision queues.
//
// ── WHAT DID NOT CHANGE ─────────────────────────────────────────────────────
// Both are still declared ONCE and shared, so no department can grow a private copy; every row keeps
// the exact `can` predicate that mirrors its server gate, so who sees what is unchanged; and
// `/tasks/my` is the same route it always was. This is a regrouping, not a permission change.
//
// ── AND WHY REQUESTS IS STILL THE ACCORDION ─────────────────────────────────
// It still holds three-to-four rows and still collapses. MY_WORK holds one or two and is flat — a
// one-item accordion is a click in front of a door, which is what §9 calls a workflow-only page
// masquerading as navigation. Registration & Sales and Customer Support already use a flat
// "My work" heading; this makes the other four departments agree with them.
const MY_WORK = { section: "My work", items: [D.myTasks] };

const REQUESTS = {
  section: "Requests",
  collapsible: true,
  icon: "bi-inbox-fill",
  // ── BD-01/BD-05 (Properties sidebar contract, owner-approved 2026-09-06) ────
  // `Documents Requested` joins this group. BD-05 defines Requests as "work or decisions requiring
  // a department admin's action", and a D-12 request Legal has forwarded is exactly that — it is
  // not one of Properties' own asset registers, which is where it used to sit.
  //
  // IT IS ADDED TO THE ONE SHARED DECLARATION, NOT COPIED INTO PROPERTIES. That is the rule this
  // group already lives by: five departments share it and the DIFFERENCES COME FROM THE PREDICATES.
  // `canFulfilDocumentRequests` admits the Properties department and the Master Admin and nobody
  // else, so Field Operations, Legal and Customer Support see no new row — exactly as
  // `canForwardLegalDocumentRequest` already gives the D-12 forwarding row to Legal alone.
  //
  // Route, guard, API, D-12 status model and the dashboard KPI that points at it are all untouched.
  items: [D.docRequests, D.completionReviews, D.reassignmentQueue, D.extensionQueue,
    D.legalDocForwarding],
};

// ── §4 · THE GENERIC `/tasks` ROW IS GONE — AND ITS ROUTE IS NOT ────────────────────────────
// `D.allTasks` ("All Tasks" → `/tasks`) used to sit in BOTH composers, which put the unscoped
// organisation-wide task list one click away in twelve profiles. §4 makes its absence from the
// sidebar mandatory. THE ROUTE STAYS REGISTERED and every existing deep link still resolves — what
// is withdrawn is the standing invitation, exactly as `ADMIN-C` already withdrew it from
// Administration. What replaces it is narrower and answers a real question: My Tasks is the
// employee's own work, and the three queues are the decisions actually waiting on an admin.

// ═════════════════════════════════════════════════════════════════════════════
// Profile 1 · ADMINISTRATION — the Master Admin's organisation-control area
// ═════════════════════════════════════════════════════════════════════════════
// Short, purposeful and free of operational modules (Part 1 §2, §3, §15). Twelve destinations, in
// the order the Administration model declares them.
// ── Administration Part 4 §1 · THE FINAL SIDEBAR ─────────────────────────────────────────────
// Ten rows, in the order §1 declares them. Deliberately small: a control area whose menu takes a
// scroll to read is not a control area.
//
// ── NOTHING WAS DELETED, ONLY RE-HOMED (Part 4 §2) ──────────────────────────
// The owner's §2 ruling forbids dropping the capabilities Part 1 preserved, so every one of them is
// still reachable, one click from a sidebar row:
//   Report Catalogue · Analytics · Report Builder · Saved Reports ·
//   Export Centre · Scheduled Reports · Audit Reports        →  Performance
//   Users & Access · Department Admins                       →  Password Reset
//   Configuration · Department & Employee Performance boards →  Dashboard
// The contract test asserts each of them is still reachable, so a future edit cannot strand one.
//
// ═══ ADMINISTRATION PART 2 · THE SIDEBAR STOPS BEING A LIST AND BECOMES A TREE ═══════════════
//
// §4 supersedes Part 4 §1's flat ten/eleven rows. The re-homing above was a way of NOT deleting a
// capability once it lost its row; §4 gives those capabilities rows again, under headings that say
// what they are. Every group below uses the ONE collapsible mechanism Part 1 built — no second
// accordion, no Administration-specific renderer — and this array is still the only place the
// Administration menu is declared.
//
// ── EVERY HOSTING LINK IS DELIBERATELY LEFT IN PLACE (§25, §30) ──────────────
// A capability that gains a sidebar row here does NOT lose the page link that carried it while it
// had none. `ADMIN-A3`/`ATT-E3` pin those links as TEXT inside Performance.jsx, Overview.jsx and
// PasswordReset.jsx, and §25's ordering rule is explicit that hosting lists come out only after the
// contract is re-pinned — which Part 2 does not do. So the doors are now two, never zero, and
// removing the second one is a later part's decision, not a side effect of this one.
//
// ── WHY PERFORMANCE AND ACTIVITY ARE STILL HERE ─────────────────────────────
// §4's target IA does not list them. They stay, under §28/§29, because dropping their rows would
// STRAND them: each has exactly one door in the whole application and that door is this row.
// §29 says so of Activity by name — "do not remove its only door merely because Audit Trail has been
// added" — and Performance is additionally the PAGE that hosts eight other capabilities' links, so a
// Performance with no row would take `ADMIN-A3`'s whole reachability guarantee down with it.
// Audit Trail (`/audit`) is a DIFFERENT destination and does not replace either: Activity is
// Administration's own operational feed, Audit Trail is the frozen Module 13 audit record.
//
// ── WHAT §4 ASKED FOR AND THIS DOES NOT CONTAIN ─────────────────────────────
// "Operational Reports", "Roles & Permissions" and "Access Control" have NO registered route, page or
// report category anywhere in the repository. §0 forbids inventing a page to satisfy a sidebar, so
// each is recorded as a gap in PART_2_ADMINISTRATION_NAVIGATION_REPORT.md rather than shipped as a
// row that leads nowhere. Note especially that report category "O" is AUDIT_COMPLIANCE, not
// "Operational" — `/reports?category=O` is Audit Reports and is not the missing destination.
export const ADMINISTRATION_NAV = [
  // ── BD-28 · MASTER ADMIN HAS PROPERTIES — OWNER-RULED (2026-09-07) ─────────
  //
  // The previous pass reported this BLOCKED: Administration Part 1 §3 forbade any
  // operating-department module here, and `ADMIN-B` pinned "Properties" in its forbidden list. The
  // owner has now ruled BD-28 explicitly — "MASTER ADMIN MUST HAVE A PROPERTIES SIDEBAR ENTRY. This
  // is an explicit frozen ruling" — so the row is added and `ADMIN-B` is narrowed to match, with the
  // ruling named at the assertion.
  //
  // ── IT IS ADMINISTRATIVE ACCESS, NOT OPERATIONAL OWNERSHIP ────────────────
  // BD-28 is equally explicit about what this does NOT bring, and none of it is added: no Property
  // Tasks, no Property Requests, no global Ventures, no global Plots. Property OPERATIONS stay with
  // the Properties department; this is the Master Admin's door to the property RECORDS they are
  // already authorised to read.
  //
  // NO PERMISSION WAS BROADENED TO ACHIEVE IT. `/properties` was already routed and the backend
  // already admits a Master Admin (`staffReadGate` on the read, and `propertyService`'s scoping
  // helpers return early for `isMasterAdmin`). Only the row was missing, so only the row was added.
  //
  // It joins the EXISTING unheaded group rather than starting a new section, so the group count and
  // the collapsible shape that several contracts assert by index are both unchanged.
  { section: null, items: [D.overview, D.properties, D.departments, D.employees] },
  { section: "Work Intelligence", collapsible: true, icon: "bi-diagram-3",
    // BD-03 (LOCKED) adds `D.decisions` FIRST in this section, ahead of the three monitoring rows.
    // What is waiting on the Master Admin personally outranks what they are watching on somebody
    // else’s behalf, and the ordering says so without a badge or a count in the sidebar.
    items: [D.decisions, D.taskMonitoring, D.bottlenecks, D.exceptions] },
  { section: "Reporting", collapsible: true, icon: "bi-graph-up",
    items: [D.adminReports, D.analytics, D.builder, D.saved, D.exports, D.adminSchedules,
      D.performance, D.activityCentre] },
  { section: "Users & Access", collapsible: true, icon: "bi-shield-lock", items: [D.accessUsers] },
  { section: "Security", collapsible: true, icon: "bi-key", items: [D.passwordReset] },
  // §45 — Document Requirements sits beside Configuration under System, which is what it is: global
  // configuration the Master Admin owns. The Configuration hub keeps its own tile to the same page,
  // so the capability now has two doors and never zero (the standing "a row does not replace a
  // hosting link" rule this file already follows elsewhere).
  { section: "System", collapsible: true, icon: "bi-sliders", items: [D.configuration, D.documentRequirements] },
  // CS6 Phase 7 · U1.5 / S-5 (owner ruling 2026-10-01): the Master Admin reaches Applications and Historical Customers
  // from the menu — READ-ONLY; every Customer Support act is still refused by the server (P7-R19).
  { section: "Customer Onboarding", collapsible: true, icon: "bi-person-vcard", items: [D.csApplications, D.csHistoricalCustomers] },
  { section: null, items: [D.notifications, D.auditTrail] },
];

// ═════════════════════════════════════════════════════════════════════════════
// Profile 2 · the OPERATING DEPARTMENT admins — one profile each
// ═════════════════════════════════════════════════════════════════════════════
// Each leads with its OWN department's surfaces — the ones that admin opens every morning — and then
// shares the operating-department tail. Composed rather than copy-pasted so the five cannot silently
// drift apart; what a department declares is its heading and its lead rows, which is exactly what
// differs between them today.
// `queues` — Part 2 §34. An OPTIONAL extra section, for a department whose own operational queues
// are neither task decisions (those are `REQUESTS`) nor asset browsing (that is "Department work").
// Customer Support is the only department that has any today.
//
// ── IT IS INSERTED AFTER `REQUESTS`, AND THAT POSITION IS LOAD-BEARING ──────
// Two frozen contracts read this shape BY INDEX: `lead(u)` in
// `adminPanelNavigationContracts.test.js` asserts `visibleSections(u)[1].items` exhaustively for all
// four shared-profile admins, including Customer Support. Adding the section at index 3 leaves
// indexes 0 and 1 byte-identical, so BOTH contracts still pass UNMODIFIED — no assertion was
// relaxed, re-pinned or deleted to make room for this.
//
// ── AND IT IS FLAT, NOT COLLAPSIBLE, ALSO DELIBERATELY ─────────────────────
// `adminPanelCollapsibleNavigation.test.js` pins that NO department profile declares a collapsible
// section of its own — the shared `REQUESTS` constant is the one per-department accordion, so a
// second one here would be exactly the drift that contract exists to prevent. R&S's own sections
// ("Sales", "Registration", "Records") are flat for the same reason.
// ── PART 4 · THE COMPOSER IS NOW THE FALLBACK, NOT EVERY DEPARTMENT'S SHAPE ─────────────────
//
// This function used to produce EVERY department admin menu, which is exactly what Part 4 exists to
// end. Three audit findings describe what that produced:
//
//   SIDEBAR-A21  `ASSETS` (Properties/Ventures/Plots/Verifications) was headed "Department work" for
//                the Legal and Customer Support admins, who operate none of the four.
//   SIDEBAR-A22  `BUSINESS` was headed "Reference" for every actor, including the department that
//                OWNS one of those rows.
//   SIDEBAR-A24  `My Tasks` sat inside `Requests` beside three administer-gated queues.
//
// The four operating departments now declare their own arrays, so a heading means what it says.
// THIS COMPOSER SURVIVES FOR THE FALLBACK PROFILE ALONE — a department this file does not yet name
// still gets a working, correctly gated menu rather than nothing.
function departmentAdminProfile({ heading, lead, work = ASSETS, queues = [] }) {
  return [
    { section: null, items: [D.dashboard] },
    { section: heading, items: [...lead, D.notifications] },
    MY_WORK,
    REQUESTS,
    ...queues,
    { section: "Department work", items: work },
    { section: "Business", items: BUSINESS },
    { section: "Department records", items: RECORDS },
    { section: "Reporting", items: REPORTING },
    { section: "Distribution", items: [D.exports] },
  ];
}

// Shared tails for the department-specific profiles below, declared once so four departments cannot
// drift apart on the parts of a menu that genuinely ARE common to them.
// The frozen tiered reads (MDS 06 §10 / PD11, 07 §10, 08 §10) that `NAV-J` protects. Every
// operating department is granted a restricted read of these five, so every operating profile keeps
// them — MINUS whichever one that department itself owns, which gets a heading of its own instead.
// That is SIDEBAR-A22 fixed by re-heading rather than by deletion.
// PHASE 3.1 — `D.legal` removed with the Case register. The other four tiered reads are unchanged.
// Legacy Cleanup Phase 1B (2026-10-11): the `TIERED_REFERENCE` list itself was removed — no profile used it; the
// tiered reads are written out in each profile (e.g. `BUSINESS`, the CS "Reference" rows).

const DEPT_RECORDS = { section: "Records", items: [D.documents, D.audit] };
const DEPT_REPORTING = { section: "Reporting", items: [...REPORTING, D.exports] };
const PERSONAL = { section: "Personal", items: [D.notifications, D.myActivity] };

/** P5-D3 — every in-scope section is a named dropdown; its heading is the trigger (depth two). */
const P5_SECTION = (section, icon, items) => ({ section, collapsible: true, readerMayOpenOthers: true, icon, items });

// ═════════════════════════════════════════════════════════════════════════════
// PART 4 §10-§14 · PROPERTIES — a PROPERTY-CENTRIC workspace
// ═════════════════════════════════════════════════════════════════════════════
//
// §10: this department's business object is the PROPERTY. The asset registers stay — they are this
// department's OWN registers, not another's — and everything the shared tail contributed about other
// departments is gone from the work headings.
//
// ── PROPERTIES SIDEBAR CONTRACT (owner-approved 2026-09-06) ─────────────────
// BD-01  `Documents Requested` moved from `Property work` into `Requests`. It is work LEGAL asks
//        this department to supply, not one of its registers. Route, guard, API, D-12 status model
//        and the dashboard KPI that points at it are all untouched — only the heading it sits under.
// BD-02  `Department Queue` moved from `Property work` into its own `Department` heading, which is
//        the shape `FIELD_OPERATIONS_NAV` already uses. It is NOT merged into `My work`: SIDEBAR-A24
//        recorded mixing a personal queue with administer-gated ones as a defect, and My Tasks
//        (personally assigned) and the Department Queue (department administration) stay distinct.
// BD-05  `Requests` means "work or decisions requiring Properties Admin action" — the D-12 queue and
//        the three task-decision views, kept together.
// BD-07  NO BADGES. No row carries a count; the dashboard KPIs remain the authoritative figures.
//
// ── WHY THESE SECTIONS ARE DECLARED HERE RATHER THAN SHARED ─────────────────
// `MY_WORK`, `REQUESTS`, `DEPT_RECORDS`, `DEPT_REPORTING` and `PERSONAL` are shared by
// Administration, Field Operations, Legal and Customer Support. Editing them to reshape PROPERTIES
// would have silently reshaped four other departments' sidebars. So Properties declares its own
// section objects and the shared constants are left exactly as they were — `TIERED_REFERENCE` is
// still reused verbatim, because BD-04 keeps that list unchanged.
//
// `Requests` here omits `D.legalDocForwarding`, which the shared group carries for the LEGAL admin.
// That is not a removal: `canForwardLegalDocumentRequest` already filtered it out for every
// Properties actor, so the rendered output is identical.
//
// ── EVERY GROUP IS AN ACCORDION (approved §2/§3) ────────────────────────────
// Declaring `collapsible: true` is the whole change — `resolveOpenSection` already makes THE ROUTE
// WIN over the reader's last choice, and `toggleOpenSection` already enforces one-open-at-a-time.
// No second sidebar renderer and no new accordion mechanism is introduced.
//
// WHAT IS DELIBERATELY ABSENT, AND WHY:
//   Users & Employees — organisation control, which Administration owns (§3).
//   Data Completion — BD-06: NO ROUTE FOR IT EXISTS and none may be invented. The employee reaches
//     the work through My Tasks -> `/tasks/:id`, which is the canonical door.
// ═════════════════════════════════════════════════════════════════════════════
// THE PROPERTIES SIDEBAR UX RULE (owner-approved 2026-09-06)
// ═════════════════════════════════════════════════════════════════════════════
//
//     2+ visible children  ->  accordion
//     exactly 1 child      ->  DIRECT NAVIGATION (no chevron, nothing to expand)
//     0 children           ->  the section is not rendered at all
//
// The point is to delete the pointless `expand -> click` on a section that leads to one place.
// `Department` and `My work` hold one row each, so they are FLAT: the existing flat branch in
// `Sidebar.jsx` renders a heading plus a real `NavLink`, which IS direct navigation and already
// carries the active highlight and `aria-current`. Nothing new was built to achieve it.
//
// THE COUNT IS THE VISIBLE COUNT. `visibleSections` applies each row's `can` predicate BEFORE the
// sidebar renders, and it preserves `collapsible`, so a group that filters down to one row for some
// actor would still declare the flag. Properties has no such group today — every row in the five
// accordion groups below is visible to every actor that reaches this profile — and
// `propertiesSidebarContract.test.js` asserts the rule against the RESOLVED sections for both roles,
// so a future predicate change that shrank a group to one row would be caught there.
//
// ── SHARED ITEMS, LOCAL PRESENTATION ───────────────────────────────────────
// `TIERED_REFERENCE`, `DEPT_RECORDS`, `DEPT_REPORTING` and `PERSONAL` stay declared exactly ONCE and
// their ROW LISTS are reused verbatim here — so Properties can never drift from the other
// departments about WHICH rows a section holds. What is local is only how the section is presented,
// which is what this ruling is about. Editing the shared constants instead would have turned Field
// Operations, Legal, Customer Support and Administration into accordions too, and their navigation
// is frozen.
// ═════════════════════════════════════════════════════════════════════════════
// PROPERTY PHASE 1 §5/§6 · THE PROPERTY DEPARTMENT'S OWN ROWS
// ═════════════════════════════════════════════════════════════════════════════
//
// DECLARED LOCALLY, NEVER BY EDITING THE SHARED CONSTANTS. `REQUESTS`, `TIERED_REFERENCE` and
// `DEPT_REPORTING` above are shared by five departments, and this file already records the rule for
// exactly this situation: "IT IS ADDED TO THE ONE SHARED DECLARATION, NOT COPIED INTO PROPERTIES …
// the DIFFERENCES COME FROM THE PREDICATES." That rule holds when a change is common to all five.
// Phase 1 §5 is the opposite case — it reshapes PROPERTIES ALONE — so editing the shared arrays
// would silently reshape Field Operations, Legal, Customer Support and Administration too. The same
// supersession shape `PROPERTIES_NAV` already uses for its accordion applies here.
//
// NOTHING BELOW GRANTS ANYTHING. Every row points at a route that already exists and already carries
// its own `RoleRoute` guard, and the server re-checks every request regardless.

// §5 — `Unassigned Work` IS the former standalone `Department Queue` row, re-homed rather than
// dropped. Same route, same guard, same screen: what changes is that the department's decision
// queues are now one group instead of one loose row plus a group of three, which is what BD-05
// defines a Request as ("work or decisions requiring a department admin's action") and what
// SIDEBAR-A24 recorded as a defect when the two were mixed under `My work`.
const PROPERTIES_UNASSIGNED = {
  ...D.deptQueue,
  label: "Unassigned Work",
};

// §5 — the Requests group as Properties declares it. The four shared rows plus the re-homed queue.
// `D.legalDocForwarding` is deliberately absent: it is Legal's forwarding decision, gated by
// `canForwardLegalDocumentRequest`, so a Properties actor was never offered it and its removal from
// this local copy changes nothing an actor could see.
// DERIVED FROM THE SHARED GROUP, NOT A SECOND ONE. The envelope — the heading, the `collapsible`
// flag and the icon — is spread from `REQUESTS`, so this is still the ONE task-decision accordion the
// estate declares and a future change to the shared group's presentation follows here automatically.
// Only the row list is local, which is exactly what §5 reshapes. `P2-R2` asserts that no department
// declares a second collapsible `Requests`, and this deliberately does not.
//
// `D.legalDocForwarding` is absent, as it is from every non-Legal menu: it is gated by
// `canForwardLegalDocumentRequest`, so a Properties actor was never offered it and omitting it here
// changes nothing an actor could see.
// PHASE 2 §13 — the child order is the one §2 names: Documents Requested, Completion Reviews,
// Unassigned Work, Reassignment Requests, Extension Requests. Reordering only; every row is the same
// destination with the same guard it had in Phase 1.

// PHASE 1 §6 declared `PROPERTIES_MY_DOC_REQUESTS` here — `/properties/document-requests?mine=1`,
// labelled `My Document Requests` — because the employee had no door to their document-request work
// at all. PHASE 2 §6 replaces it with `My Tasks -> Document Tasks`, which reaches the same work
// through the TASK, where §10/§11 require the upload to happen. The declaration is removed rather
// than left unused; the ROUTE and its guard are untouched and `?mine=1` still resolves.

// ── PHASE 2 §6/§7/§20 · MY TASKS, SPLIT BY THE KIND OF WORK ────────────────
//
// The employee's list mixed Data Completion and Document Supply with nothing to tell them apart, so
// "what am I actually being asked to do?" could only be answered by opening each task. §7 requires
// the two to be immediately distinguishable and §20 requires the filters to be real.
//
// THEY ARE `?kind=` VIEWS OF THE ONE EXISTING SCREEN, exactly as the Requests group's three
// `?view=` rows are views of one task board. Same route, same guard; the SERVER classifies each task
// from the document-request relationship and applies the filter, so neither row is a client-side
// slice of a list the employee was handed in full.
//
// `isRowActive` compares the query string, so the two highlight independently while sharing a screen.
const PROPERTIES_DATA_COMPLETION = {
  ...D.myTasks,
  to: "/tasks/my?kind=DataCompletion",
  label: "Data Completion",
  icon: "bi-clipboard-data",
};
const PROPERTIES_DOCUMENT_TASKS = {
  ...D.myTasks,
  to: "/tasks/my?kind=DocumentSupply",
  label: "Document Tasks",
  icon: "bi-file-earmark-arrow-up",
};

// §5/§6 — Reporting, scoped. `Report Catalogue` is relabelled `Property Reports` because that is what
// a Properties actor may actually run: the catalogue offers 110 rows and refuses all but a handful
// for this department, so the generic label promised a breadth the server does not grant. The ROW,
// ROUTE AND GUARD ARE UNCHANGED — this renames a door, it does not narrow one, and the catalogue's
// own server-side scoping remains the authority.
//
// `Analytics` is withdrawn from Properties (§44's merge): it is a second door onto overlapping report
// surfaces. `canViewReports` is untouched and `/analytics` keeps its guard, so a link still resolves.
const PROPERTIES_REPORTS = { ...D.catalogue, label: "Property Reports" };

// ── FIELD PHASE 2 §8/§51/§52 · THE FIELD REPORTING GROUP ────────────────────────────────────────
//
// §8 names four rows: Field Reports, Report Builder, Saved Reports, Export Centre.
//
// `Field Reports` IS THE CATALOGUE UNDER THE NAME §8 GIVES IT — the same device and the same reason
// `PROPERTIES_REPORTS` above already uses: a generic "Report Catalogue" promises every department's
// reports, and the catalogue the reader actually reaches is scoped by the server to what their role
// may run. Same route, same predicate, same page; only the label.
//
// `Analytics` LEAVES, exactly as it left the Properties menu: §8's four rows do not include it, and
// the catalogue is where a Field report is found. THE ROUTE AND THE GRANT ARE UNTOUCHED — `/analytics`
// stays registered and `canViewReports` still admits Field Operations.
//
// PHASE 1 F-P0-3 IS WHAT MAKES THIS ROW HONEST. Until `REPORT_SOURCE.VERIFICATIONS` was declared
// Field Operations', all seven §H reports answered this department's own Admin 403 while the
// catalogue advertised them as Available. Naming the row "Field Reports" before that was fixed would
// have been a label on a locked door.
const FIELD_REPORTS = { ...D.catalogue, label: "Field Reports" };
const FIELD_REPORTING = {
  section: "Reporting",
  collapsible: true,
  readerMayOpenOthers: true, // P5-D3 — Field profiles only; see resolveOpenSection.
  icon: "bi-graph-up",
  items: [FIELD_REPORTS, D.builder, D.saved, D.exports],
};

// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes Property Phase 2 §2/§6) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const PROPERTIES_NAV = [
  { section: null, items: [D.dashboard] },
  P5_SECTION("Work", "bi-briefcase", [D.myTasks, PROPERTIES_UNASSIGNED, D.docRequests]),
  P5_SECTION("Reviews", "bi-clipboard-check", [D.completionReviews]),
  P5_SECTION("Approvals", "bi-check2-square", [D.reassignmentQueue, D.extensionQueue]),
  P5_SECTION("Records", "bi-archive", [D.properties, D.historicalSales, D.audit]),
  P5_SECTION("Reporting", "bi-graph-up", [PROPERTIES_REPORTS, D.builder, D.saved, D.exports]),
  P5_SECTION("Configuration", "bi-sliders", [D.propertyRequirements, D.documentReusability]),
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// ═════════════════════════════════════════════════════════════════════════════
// PART 4 §19-§24 · FIELD OPERATIONS — a VERIFICATION-CENTRIC workspace
// ═════════════════════════════════════════════════════════════════════════════
//
// §19: the business object is the FIELD VERIFICATION, and §20 names the board states its admin works
// through. All five rows are EXISTING surfaces — `/verifications/queue` is the assignment board, the
// three state rows are `verificationStatus` views of `/verifications`, and `/verifications/handoff`
// is the one-way door into Legal that §24 protects.
//
// `Properties` stays because a verification is ABOUT a property and the admin needs the register to
// find one. Ventures and Plots do not: a verification is assigned at property or plot level from
// inside the queue, never browsed for.
// ── FIELD PHASE 2 §8/§9 · THE DEPARTMENT'S MENU IS ORGANISED AROUND WORK ────────────────────────
//
// §8 asks for a sidebar built around WORK rather than entity administration, and §9 forbids assuming
// the Admin and the Employee should see the same one. The two profiles below are declared in full
// for that reason — the shared composers cannot express "the same heading, different children".
//
// ── ONE ITEM IS A LINK, NOT AN ACCORDION (§8) ───────────────────────────────
// §8 states the rule plainly: "Single-item entries do NOT need dropdowns … Do not create useless
// dropdowns containing one item." It is applied here EVEN WHERE §8's own sketch draws a group:
//   `Handoff ▾ → Legal Handoff` holds one row, so `Legal Handoff` is a top-level link.
//   `Records ▾` holds two for the Admin and one for the Employee, so it collapses for the Admin and
//   is a flat link for the Employee.
// The rule is applied to the RESULT, not to the sketch — which is the same one-row rule the
// Properties sidebar already lives by and `P1-K4` already pins.
//
// ── WHAT LEAVES, AND WHY IT IS NOT A LOSS OF ACCESS ─────────────────────────
// The four cross-department tiered reads — Customers, Sales & Bookings, Reallocations, Customer
// Services — leave BOTH Field menus. §8's target structure does not carry them, and a field
// operative's work is the property in front of them, not another department's register. THE FROZEN
// GRANT IS UNTOUCHED: `canViewCustomers`, `canViewSales`, `canViewReallocations` and
// `canViewServiceRequests` still admit Field Operations, every route stays registered and guarded,
// and the server answers a Field caller exactly as before. What is withdrawn is the standing
// INVITATION to browse them — precisely the treatment PROPERTY PHASE 1 §6 already gave the same four
// rows in the same way, and `NAV-J` asserts both halves for both departments.
//
// `Department Queue` also leaves: §8 replaces the generic 13T board with the three decision queues a
// Field Admin actually acts on, all of which are views of that same route.
// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes Field Phase 2 navigation) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const FIELD_OPERATIONS_NAV = [
  { section: null, items: [D.dashboard] },
  P5_SECTION("Work", "bi-briefcase", [D.myTasks, D.fieldQueue, D.fieldRevisits, D.legalHandoff]),
  P5_SECTION("Reviews", "bi-clipboard-check", [D.fieldCompletionReviews]),
  P5_SECTION("Approvals", "bi-check2-square", [D.reassignmentQueue]),
  P5_SECTION("Records", "bi-archive", [D.properties, D.fieldVerificationRecords, D.audit]),
  FIELD_REPORTING,
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// ═════════════════════════════════════════════════════════════════════════════
// PART 4 §25-§37 · LEGAL — a specialised professional workspace
// ═════════════════════════════════════════════════════════════════════════════
//
// §27's shape. The decision queues this admin owns come first — intake, NOC review, the department
// task queue — then the department's own records: the case register and its reporting board.
//
// WHAT STAYS OUT OF THE SIDEBAR, AND WHY (§9, §32, §33, §34):
//   Legal Review     `/legal/review/:taskId` is TASK-DRIVEN and opens from My Tasks. A permanent row
//                    would need a task id it cannot have.
//   Legal Issues     embedded in the review workspace; §33 says preserve that.
//   Legal Clearance  a workflow decision inside the review, not a page (§34).
//   Case create,     `/legal/new`, `/legal/:id/assign` and `/legal/:id/reassign` are contextual
//   assign, reassign actions reached from the case (§29).
//   D-12             stays where Part 3 put it, under Requests (§37) — not duplicated here.
//
// ── LEGAL PHASE 2 §9 (owner, final) · THE LEGAL ADMIN'S WORK-ORIENTED SIDEBAR ─────────────────────
// Dashboard · Work ▾ (Legal Intake, My Tasks, Document Requests) · NOC Review · Records ▾ (Legal
// Reviews, Legal Issues, Audit & Timeline) · Reporting ▾ (Legal Reports, Report Builder, Saved
// Reports, Export Centre) · Personal ▾ (Notifications, My Activity).
//
// THE SINGLE-ITEM RULE: a group of one is a direct link, so `Reviews ▾ └ NOC Review` is drawn as the
// plain row `NOC Review`. Groups of two or more are dropdowns.
//
// REMOVED FROM THIS MENU (routes and backends untouched — a removed row is a withdrawn invitation,
// not a withdrawn permission): Customers, Sales & Bookings, Reallocations, Customer Services (other
// departments' registers), global Documents (Documents are contextual; Document Requests is the
// workflow), Department Queue (Legal's incoming work is Legal Intake; each task opens from My
// Tasks), the duplicate `Requests` group, `Legal Reporting` / `Report Catalogue` / `Analytics` (one
// reporting entry: `Legal Reports`). The Legal reporting board stays reachable from the dashboard.
const LEGAL_REPORTING_ITEMS = [D.legalReports, D.builder, D.saved, D.exports];

// LEGAL PHASE 5 §11/§33/§42 — the Legal Department Admin's two operational queues. Dedicated Legal
// pages (not the generic Department Queue), drawn for the Legal Admin alone — `canActOnLegalIntake`,
// the same predicate Legal Intake uses. A lawyer is refused both by the server as well as the menu.
const LEGAL_COMPLETION_REVIEWS = {
  to: "/legal/completion-reviews", icon: "bi-clipboard-check", label: "Completion Reviews",
  resource: "legalReview", action: "decide", can: canActOnLegalIntake,
};
const LEGAL_REASSIGNMENT_REQUESTS = {
  to: "/legal/reassignment-requests", icon: "bi-arrow-left-right", label: "Reassignment Requests",
  resource: "legalReview", action: "reassign", can: canActOnLegalIntake,
};

// ── LEGAL PHASE 10 §4 · COURT & LEGAL MATTERS ─────────────────────────────────────────────────────
// The Legal Admin's three rows and the Lawyer's two. Same routes: the server scopes a lawyer to the matters assigned to
// them, so "My Court Matters" and "Court Matters" are one page read by two readers. The Resolution Queue is the Legal
// Admin's decision door only.
const isLegalLawyer = (user) => isLawyer(user) && canUseLegalWork(user);
const COURT_MATTERS = {
  to: "/legal/court-matters", icon: "bi-bank", label: "Court Matters",
  resource: "legalCourtMatters", action: "read", can: canActOnLegalIntake,
};
const COURT_HEARINGS = {
  to: "/legal/hearings", icon: "bi-calendar-event", label: "Hearings",
  resource: "legalCourtMatters", action: "read", can: canActOnLegalIntake,
};
const COURT_RESOLUTION_QUEUE = {
  to: "/legal/court-matters/resolutions", icon: "bi-clipboard2-check", label: "Resolution Queue",
  resource: "legalCourtMatters", action: "decide", can: canActOnLegalIntake,
};
const MY_COURT_MATTERS = {
  to: "/legal/court-matters", icon: "bi-bank", label: "My Court Matters",
  resource: "legalCourtMatters", action: "read", can: isLegalLawyer,
};
const MY_HEARINGS = {
  to: "/legal/hearings", icon: "bi-calendar-event", label: "My Hearings",
  resource: "legalCourtMatters", action: "read", can: isLegalLawyer,
};

// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes Legal Phase 2 §9) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const LEGAL_NAV = [
  { section: null, items: [D.dashboard] },
  P5_SECTION("Work", "bi-briefcase", [D.legalIntake, D.myTasks, D.legalWorkRequests]),
  P5_SECTION("Reviews", "bi-clipboard-check", [LEGAL_COMPLETION_REVIEWS, D.nocRequests]),
  P5_SECTION("Approvals", "bi-check2-square", [LEGAL_REASSIGNMENT_REQUESTS]),
  P5_SECTION("Court & Legal Matters", "bi-bank", [COURT_MATTERS, COURT_HEARINGS, COURT_RESOLUTION_QUEUE]),
  P5_SECTION("Records", "bi-archive", [D.legalReviews, D.legalIssues, D.audit]),
  P5_SECTION("Reporting", "bi-graph-up", [D.legalDashboard, ...LEGAL_REPORTING_ITEMS]),
  P5_SECTION("Configuration", "bi-sliders", [D.legalRequirements, D.documentReusability]),
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// ── LEGAL PHASE 2 §10 · THE LAWYER'S SIDEBAR ─────────────────────────────────────────────────────
// The Lawyer and the Legal Employee now take DIFFERENT profiles (the C3 question Part 4 left open is
// answered by Phase 2 §10/§11). My Tasks is the Lawyer's work door. No Legal Intake (the Legal
// Admin's queue — the server refuses a lawyer), no NOC Review (the Legal Admin's decision). The
// `Audit & Timeline` row keeps its own predicate: the server gives the audit trail to admins only,
// so a lawyer's Records shows the two rows they can actually open.
// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes Legal Phase 2 §10) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const LAWYER_NAV = [
  { section: null, items: [D.dashboard] },
  P5_SECTION("Work", "bi-briefcase", [D.myTasks, D.legalWorkRequests]),
  P5_SECTION("Court & Legal Matters", "bi-bank", [MY_COURT_MATTERS, MY_HEARINGS]),
  P5_SECTION("Records", "bi-archive", [D.legalReviews, D.legalIssues, D.audit]),
  P5_SECTION("Reporting", "bi-graph-up", LEGAL_REPORTING_ITEMS),
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// ═════════════════════════════════════════════════════════════════════════════
// Registration & Sales Part 1 §5 · THE FOCUSED SALES SIDEBAR
// ═════════════════════════════════════════════════════════════════════════════
//
// This profile is DECLARED IN FULL rather than composed from `departmentAdminProfile`, and that is
// the whole point of it. The shared shape appends `ASSETS` (Properties, Ventures, Plots,
// Verifications), `BUSINESS` (Legal, Customers, Sales & Bookings, Reallocations, Customer Services)
// and `RECORDS` (Documents, Audit, Users) to every department — and §5 forbids nearly all of them
// here by name: no Legal, no Property Department, no Vendors, no Data Completion, no Lawyer Tasks,
// no KYC, no Customers, no Payments, no Registration, no Refunds, no Cancellation. §5 also states
// the reason plainly: "do not expose unrelated departmental navigation merely because the current
// user has broader permissions."
//
// NOTHING IS DELETED AND NO ROUTE IS BROKEN. `/legal`, `/customers`, `/properties`, `/documents`,
// `/audit`, `/sales/bookings` and every other destination stay registered, stay reachable by link
// and bookboard, and the server's permissions are untouched — a Sales Admin who types one is
// answered by the API exactly as before. What is withdrawn is the standing INVITATION to leave the
// sales workspace, which is the same treatment Field Operations Part 1 §15 gave `/verifications`
// and Administration Part 1 §3 gave the operating modules.
//
// EIGHT ROWS, IN §5's OWN ORDER AND ITS OWN GROUPING.
// PART 5 ADDS ONE SECTION — "Completion" — and moves nothing. Every Part 1 row keeps its group, its
// order and its route, so an admin who knew this menu yesterday finds it unchanged and simply has two
// more destinations at the end. §2's grouping: the work that FINISHES a sale sits together, after the
// work that starts one.
// ── PART 6 §30 · REGROUPED INTO THE TWO HALVES OF THE DEPARTMENT'S NAME ──────────────────
//
// P6-D4. The five sections above were Part 1's inventory shape with Part 5's "Completion" appended,
// and three destinations §30 names — Customers, Bookings, Payments — had no row at all. §30 asks for
// the menu to read as the department works: SALES starts a sale, REGISTRATION finishes it.
//
// NOTHING IS REMOVED. Every Part 1 and Part 5 row survives with its route and its gate; they are
// regrouped, and the rows that were missing are added. `Ready for Sale`, `Available`, `Sales Tasks`,
// `Plots`, `Employees` and `Advance Configuration` all keep the admin gate they always had.
//
// STILL NO OTHER DEPARTMENT. §30's exclusion list is honoured exactly as Part 1 §5 wrote it: no
// Legal, no Lawyer Tasks, no Legal Issues, no Property Data Completion, no Vendors, no
// Administration, no HR, no Accounting, no Master Admin configuration. The rows restored below are
// this department's own surfaces, not a re-import of the shared cross-department shape.
// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes R&S Parts 1–3 navigation; P5-D1 retires the purchase-era doors) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const REGISTRATION_SALES_NAV = [
  { section: null, items: [D.salesDashboard] },
  // PART 9 FINAL (2026-10-11; owner spec §3.1–§3.2; MDS CS6 §50.11) — the reallocation case and its registration, two destinations.
  P5_SECTION("Work", "bi-briefcase", [D.salesAvailableWork, D.salesAllocation, D.salesDeptTasks, D.salesRegistrationWork,
    { ...D.reallocations, label: "Reallocation" }, D.reallocationRegistrations]),
  P5_SECTION("Reviews", "bi-clipboard-check", [D.salesPaymentVerification, D.salesFinancialCompletion, D.salesNocRequests]),
  P5_SECTION("Approvals", "bi-check2-square", [D.salesDiscounts, D.salesCommercialChanges, D.salesPaymentExtensions, D.salesReassignments]),
  P5_SECTION("Records", "bi-archive", [D.salesTransactions, D.salesHistory, D.salesEmployees]),
  P5_SECTION("Reporting", "bi-graph-up", [D.catalogue, D.builder, D.saved, D.exports]),
  P5_SECTION("Configuration", "bi-sliders", [D.registrationRequirements, D.salesAdvanceConfig, D.documentReusability]),
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// Part 2 §34 · Customer Support gains its four operational queues. The reasoning sits INSIDE the
// declaration below rather than above it — `salesInventory.test.js` reads everything between
// `export const REGISTRATION_SALES_NAV` and the next `export const` as the SALES profile, so prose
// parked here is scanned as if it were that department's configuration.
// ═════════════════════════════════════════════════════════════════════════════
// PART 4 §38-§44 · CUSTOMER SUPPORT — a SERVICE-REQUEST-CENTRIC workspace
// ═════════════════════════════════════════════════════════════════════════════
//
// §38: this department's business object is the SERVICE REQUEST, and it must not take the generic
// fallback shape (SIDEBAR-A31). Part 2 gave it the four operational queues; Part 4 finishes the job
// by declaring the profile outright, so the queues sit in the department queue where §39 puts them
// and the department's own reference surfaces get a heading of their own.
//
// SIDEBAR-A11 IS CLOSED HERE. The department's own module used to be a single row called "Customer
// Services" inside a group called "Business" — its own workspace, filed under a heading meaning
// "other departments". It is now `All Requests` inside `Customer Support`.
//
// §49's removals: Properties, Ventures, Plots and Verifications are gone. Property context is
// reached CONTEXTUALLY from an authorised service request, which is what §49 asks for; the routes
// stay registered and the server's permissions are untouched.
// ── PART 2 (owner, 2026-10-05) · BUSINESS REQUESTS ARE NOT OPERATIONAL TASKS ─────────────────────────────────────
// Customer Support's menu separates the two in name as they are separate in data: "Requests" are the BUSINESS RECORDS
// Customer Support owns (service requests, historical applications, NOC requests, reallocations); "Customer Support
// Tasks" are the universal 13T work items the department executes. NAVIGATION ONLY — every row opens a route that
// already exists, the server still decides what each reader sees, and no API, page family or task system is added.
//
// The rows are LOCAL renamings of the shared vocabulary (the PROPERTIES_UNASSIGNED device): the Master Admin's
// Administration profile keeps `D.csApplications` as "Applications", untouched.
//   NOC Requests  = the request register filtered to the NOC service type. The space is PERCENT-ENCODED, because
//                   `isRowActive` compares the query verbatim against the normalised address bar.
//   Team Tasks    = `GET /tasks` — the existing department scope (BD-4). A Department Admin reads their department's
//                   work there; an employee would read only their own, so the row is the admin's alone.
//   Queue         = the existing 13T department queue (unassigned work), unchanged.
// CS6 Phase 6 (R8) / Phase 7 (U1.4) are preserved: Historical Applications for both, Historical Customers for the Admin.
const CS_CUSTOMER_REQUESTS = { ...D.csAllRequests, label: "Customer Requests" };
const CS_HISTORICAL_APPLICATIONS = { ...D.csApplications, label: "Historical Applications" };
const CS_NOC_REQUESTS = {
  to: "/service-requests?type=NOC%20Request", icon: "bi-patch-check", label: "NOC Requests",
  resource: "serviceRequests", action: "read", can: canViewServiceRequests,
};
const CS_REALLOCATION = { ...D.reallocations, label: "Reallocation" };
const CS_TEAM_TASKS = {
  to: "/tasks", end: true, icon: "bi-people", label: "Team Tasks", resource: "tasks", action: "administer", can: canAdministerTasks,
};
const CS_TASK_QUEUE = { ...D.deptQueue, label: "Queue" };
const CS_REQUESTS = (lead = []) => ({
  section: "Requests", items: [...lead, CS_CUSTOMER_REQUESTS, CS_HISTORICAL_APPLICATIONS, CS_NOC_REQUESTS, CS_REALLOCATION],
});

export const CUSTOMER_SUPPORT_NAV = [
  { section: null, items: [D.dashboard] },
  CS_REQUESTS(),
  // The request register's own monitoring views — business-record views, kept beside the requests they watch.
  { section: "Request monitoring", items: [D.csNewRequests, D.csSlaDue, D.csEscalations, D.csAwaitingClosure] },
  { section: "Customer Support Tasks", items: [D.myTasks, CS_TEAM_TASKS, CS_TASK_QUEUE] },
  // The decisions waiting on the CS Admin about TASKS (review, reassignment, extension) — the shared approval queues.
  { section: "Task approvals", items: [D.completionReviews, D.reassignmentQueue, D.extensionQueue] },
  { section: "Customers", items: [D.customers, D.csHistoricalCustomers, D.serviceCatalogue] },
  // PHASE 3.1 — the Case register is no longer a cross-department reference.
  { section: "Reference", items: [D.sales] },
  DEPT_RECORDS,
  DEPT_REPORTING,
  PERSONAL,
];

/** A department this file does not yet name still gets a working, correctly gated menu. */
export const GENERIC_DEPARTMENT_NAV = departmentAdminProfile({
  heading: "My department",
  lead: [D.deptQueue],
});

// ═════════════════════════════════════════════════════════════════════════════
// Profile 3 · the EMPLOYEE (and Lawyer) profiles — one per department
// ═════════════════════════════════════════════════════════════════════════════
// Leads with what this person has been ASSIGNED. The administrative destinations are STRUCTURALLY
// ABSENT — no department queue, no verification queue, no user administration, no audit trail, no
// schedules — rather than present-and-filtered. A Lawyer is an individual contributor working
// assigned cases, so Legal's employee profile is theirs too.
//
// ── THERE IS ONE WAY IN TO FIELD VERIFICATION (Field Operations Part 1 §15) ──
// `D.verifications` IS DELIBERATELY ABSENT from "Assigned work". Field verification reaches an
// employee as a TASK: assignment opens one, it appears under My Tasks, and the verification is done
// from inside it. A standalone entry gave employees a SECOND route to the same work — one that
// skipped the task's accept/progress/submit lifecycle, its deadline, and its extension and
// reassignment requests. NOTHING IS DELETED AND NO ROUTE IS BROKEN: `/verifications` stays
// registered, stays reachable by link and bookmark, and the server's read permissions are untouched.
// What is removed is the standing INVITATION to start there.
// ── PART 4 · THE EMPLOYEE COMPOSER IS ALSO THE FALLBACK NOW ─────────────────────────────────
//
// It carried two of the audit's misplacements for every operating-department employee:
//   SIDEBAR-A23  "Assigned work" held Properties/Ventures/Plots — REGISTERS, not assignments. An
//                employee's assignments are their TASKS, and the heading said otherwise.
//   SIDEBAR-A22  "Reference" held all five BUSINESS rows, including the one their own department owns.
//
// The three operating-department employees now declare their own arrays; this composer survives for
// GENERIC_EMPLOYEE_NAV, the fallback for a department this file does not name.
function employeeProfile({ heading, assigned = [D.properties] }) {
  return [
    { section: null, items: [D.dashboard] },
    { section: heading, items: [D.notifications, D.myActivity] },
    MY_WORK,
    REQUESTS,
    { section: "Assigned work", items: assigned },
    { section: "Reference", items: BUSINESS },
    { section: "Records", items: [D.documents] },
    { section: "Reporting", items: REPORTING },
    { section: "Distribution", items: [D.exports] },
  ];
}

// ── PART 4 §15 · THE PROPERTIES EMPLOYEE ────────────────────────────────────────────────────
//
// §15: this employee's work stays TASK-CENTRIC. A forwarded document request reaches them as a TASK,
// so `My Tasks` is the work door and §15 says explicitly not to build a second employee
// document-request queue beside it — which is why `Documents Requested` is the ADMIN's row and is
// absent here. BD-06 re-affirms it: no Data Completion row, no D-12 row, no Recovery row, no task
// detail row. The canonical door is My Work -> My Tasks -> `/tasks/:id`.
//
// `Properties` stays as the register they need to look a property up. It is NOT a broadening: 13X
// resolves an Employee's asset reads FROM THEIR OWN ASSIGNED TASKS, so this page shows the
// properties their work names and nothing else — an employee with no assignment sees an empty list.
//
// ── REPORTING (BD-03, resolved from the implementation, not assumed) ────────
// `Report Builder` is withdrawn from this menu: authoring a report definition is a management
// activity. NOTHING IS REVOKED — `canBuildReports` is untouched and `/builder` keeps its own guard,
// so a link or bookmark still resolves exactly as before. Only the standing invitation is gone.
//
// `Saved Reports` STAYS, because the existing implementation establishes it as PERSONAL:
//   • `listDefinitions` queries `$or: [{ createdBy: me }, { shared: true }]` — my own work, plus
//     whatever a colleague explicitly published;
//   • `retireDefinition` refuses everyone but the owner ("Only the owner or a Master Admin may
//     retire this definition"), so the page offers no administrative control over anyone else's;
//   • the endpoint is open to any staff, not to an admin gate.
// A personal library is exactly the kind of thing an operator should keep.
//
// BD-04 keeps all five `TIERED_REFERENCE` reads: they are the frozen tiered grants (MDS 06 §10/PD11,
// 07 §10, 08 §10) that every operating department's staff carry, and narrowing one department's menu
// alone would make the profiles inconsistent without removing any access.
// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes Property Phase 2 §6/§7 (the owner-asked ?kind= rows are kept)) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const PROPERTIES_EMPLOYEE_NAV = [
  { section: null, items: [D.dashboard] },
  P5_SECTION("Work", "bi-briefcase", [PROPERTIES_DATA_COMPLETION, PROPERTIES_DOCUMENT_TASKS]),
  P5_SECTION("Records", "bi-archive", [D.properties, D.historicalSales]),
  P5_SECTION("Reporting", "bi-graph-up", [PROPERTIES_REPORTS, D.saved, D.exports]),
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// ── PART 4 §21-§22 · THE FIELD OPERATIONS EMPLOYEE ──────────────────────────────────────────
//
// §21 is emphatic: do NOT restore a standalone employee "Verifications" row. Field verification
// reaches this employee as a TASK, and §22's flow — task → property context → form → evidence →
// submit — happens entirely inside the task workspace. A second door would skip the task lifecycle,
// which is the single-door design `NAV-C2` already ruled deliberate (SIDEBAR-A04).
// ── FIELD PHASE 2 §9 · THE OPERATIVE'S MENU IS NOT THE ADMIN'S ──────────────────────────────────
//
// §9 is explicit: "Employee must NOT automatically receive administrative queue/assignment/review
// authority." So the `Work` group holds exactly the two rows an operative works from — the tasks
// assigned to them, and the reinspections they have been sent back to do — and NONE of the three
// decision queues or the assignment board.
//
// THAT SEPARATION IS PRODUCED BY DECLARATION HERE AND BY PREDICATE AT THE ROW. Even if this profile
// carried `D.fieldQueue`, `canAdministerVerificationQueue` would refuse it and `visibleSections`
// would drop it — and the server would refuse the request regardless. The row is absent as well,
// because offering a door that opens onto a refusal is worse than not offering it.
//
// NO AUTHORIZATION IS WIDENED TO MAKE THIS MENU WORK (§9). `fieldRevisits` carries
// `canViewVerifications` and the SERVER scopes the answer: `listRevisits` runs through
// `listVerifications`, so this operative sees their own reinspections and nobody else's.
// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes Field Phase 2 navigation) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const FIELD_OPERATIONS_EMPLOYEE_NAV = [
  { section: null, items: [D.dashboard] },
  P5_SECTION("Work", "bi-briefcase", [D.myTasks, D.fieldRevisits]),
  P5_SECTION("Records", "bi-archive", [D.properties]),
  FIELD_REPORTING,
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];

// ── PART 4 §30-§31 · THE LEGAL EMPLOYEE AND THE LAWYER ──────────────────────────────────────
//
// §30 gives this actor the door the audit recorded as missing (SIDEBAR-A05): `My Cases` → `/legal/my`,
// an existing route that scopes to `assignedLawyer = me`.
//
// MY CASES AND MY TASKS ARE BOTH HERE BECAUSE THEY ARE DIFFERENT THINGS (§31). A case is the legal
// record this lawyer owns; a task is a unit of assigned work. They overlap and neither is a subset
// of the other, so collapsing them would hide work.
//
// ── THE LAWYER MODEL IS UNTOUCHED (§26) ─────────────────────────────────────
// No Lawyer entity is created. `navigationProfileFor` still routes DESIGNATIONS.LAWYER and
// DESIGNATIONS.EMPLOYEE in the Legal department to this one profile, exactly as before — Part 4
// changes the ROWS, never the model, and C3 (whether the two should ever diverge) stays open.
//
// ── LEGAL PHASE 3 §5/§6 · THERE IS NO LEGAL EMPLOYEE ROLE — A SAFE FALLBACK, NOT A ROLE ─────────────
// The Legal department is its Department Admin and its Lawyers. This profile is NOT a role: it is what
// a LEGACY account in Legal holding the generic `Employee` designation receives — an account the staff
// form no longer offers to create, and one the server already refuses every piece of Legal work
// (`assertAssignableLegalStaff`, 422). It is never promoted to a Lawyer, and it is not handed the
// generic employee shape either, which would put other departments' registers and global Documents
// in front of it: common navigation only — the dashboard, their own tasks, reporting, personal pages.
// (Formerly LEGAL_EMPLOYEE_NAV, Legal Phase 2 §11.)
export const LEGAL_COMMON_NAV = [
  { section: null, items: [D.dashboard, D.myTasks] },
  { section: "Reporting", collapsible: true, icon: "bi-graph-up", items: [D.catalogue, D.builder, D.saved, D.exports] },
  { section: "Personal", collapsible: true, icon: "bi-person", items: PERSONAL.items },
];
// ── Registration & Sales Part 1 §23 · the SALES EMPLOYEE's surface ──────────────────────────────
//
// Declared in full, for the same reason the admin profile above is: the shared employee shape offers
// `BUSINESS` as "Reference" — Legal, Customers, Sales & Bookings, Reallocations, Customer Services —
// and §5's exclusion list applies to this department's people too, not only to its admin.
//
// §23 states exactly what a Sales Employee may do: view their OWN assigned tasks, the plot
// information on them, their status and their deadline. It states just as plainly what they may not:
// activate inventory, assign anybody, reassign another employee's task, or touch Legal. So there is
// no Ready-for-Sale row, no Available row, no Plots board, no Employees board and no History row —
// each of those is an admin surface whose API answers an employee 403, and offering a door that
// opens onto a refusal is worse than not offering it.
//
// THE SERVER IS STILL THE BOUNDARY. `/sales/inventory/ready` typed by hand is refused by
// `salesInventoryService`, not by the absence of this row.
//
// ── PART 2 §4 · THE SIX ROWS, AND WHY THEY REPLACED PART 1's FIVE ─────────────────────
//
// Part 1 gave the employee a placeholder menu — the admin's Sales Tasks board plus the generic task
// destinations — because there was no employee workspace to point at yet. Part 2 builds one, and §4
// names its rows: Dashboard, My Tasks, My Plots, Customers, Requests, Notifications.
//
// `D.salesTasks` (the ADMIN board at `/sales/tasks`) IS GONE FROM THIS PROFILE, deliberately. It is
// scoped to the caller server-side, so an employee saw only their own rows there and nothing leaked
// — but it is the admin's screen, with the admin's columns and the admin's filters, and §4 asks for
// a personal work queue instead. `/sales/employee/tasks` is that queue. THE ADMIN ROUTE IS NOT
// REMOVED and its server permissions are untouched; what is withdrawn is the standing invitation.
//
// `D.myTasks` (13T's own `/tasks/my`) is likewise dropped from the row set: a sales employee's work
// reaches them as a SALES task on their own board, and offering 13T's generic list beside it gave two
// doors to one queue — the same duplication Field Operations Part 1 §15 removed for verifications.
// The route stays registered and reachable.
// ── P5 (MDS P5 §03, owner 2026-09-21 — supersedes R&S Part 2 §4 employee navigation) ──
// Every section is a named dropdown whose heading IS the trigger (P5-D3); WORK, REVIEWS and APPROVALS
// are kept apart (P5-D2): work I execute, work someone else submitted for judgement, and requests for
// permission. Row predicates are the route predicates (R7).
export const REGISTRATION_SALES_EMPLOYEE_NAV = [
  { section: null, items: [D.empDashboard] },
  P5_SECTION("Work", "bi-briefcase", [D.empTasks, D.salesAvailableWork, D.empMyRequests,
    { ...D.reallocations, label: "Reallocation" }, D.reallocationRegistrations]), // PART 9 FINAL (MDS CS6 §50.11)
  P5_SECTION("Records", "bi-archive", [D.empMyCustomers, D.empPlots]),
  P5_SECTION("Reporting", "bi-graph-up", [D.catalogue, D.saved]),
  P5_SECTION("Personal", "bi-person", PERSONAL.items),
];
// ── Part 3 §19 · THE CUSTOMER SUPPORT EMPLOYEE TAKES ITS OWN PROFILE ─────────────────────────
//
// It was `employeeProfile({ heading: "My Customer Support work" })` — the shape shared with
// Properties, Field Operations and Legal employees. §19 separates it for two reasons, and only this
// department moves:
//
// 1. THE WORK DOOR THE RULING CREATED. A Customer Support employee's work is SERVICE REQUESTS, and
//    until Part 3 there was no row that named their own. "My work" is that door.
//
// 2. "ASSIGNED WORK" WAS NEVER THIS DEPARTMENT'S WORK. The shared shape offers Properties, Ventures
//    and Plots under that heading because it was written for the departments that operate on
//    assets. §19 names those three and says not to offer them to a Customer Support employee merely
//    because the generic configuration happens to contain them. They are REMOVED here and nowhere
//    else — the three other departments keep the composed profile untouched, which
//    `serviceRequestEmployeeScope`'s sibling contract in `adminPanelPart3Scope.test.js` pins.
//
// ── WHY THERE IS NO `Requests` ACCORDION IN THIS PROFILE ────────────────────
// For any employee the shared `REQUESTS` group resolves to exactly one row — My Tasks; the three
// decision queues are refused by `canAdministerTasks`. Keeping the accordion AND putting My Tasks in
// "My work" would be two doors to one destination, which §6 forbids. So My Tasks moves into "My
// work" beside My Requests, exactly as §19 draws it, and the one-item accordion goes. The route is
// untouched and `/tasks/my` is still reachable. Registration & Sales set this precedent: its
// employee profile also declares its own "My work" instead of taking the shared group.
//
// ── AND WHY THE REGISTER ROW IS NOT ALSO OFFERED ────────────────────────────
// `D.services` ("Customer Services" -> `/service-requests`) is in the shared `BUSINESS` tail. For
// THIS actor it now returns exactly what My Requests returns — the employee tier scopes both to
// their own assigned work — so offering both would be two rows leading to one answer. Every other
// department keeps it, because for them it is still the register.
// ── PART 4 §45-§49 · completed for this employee ────────────────────────────────────────────
//
// Part 3 built the work doors; Part 4 adds the department's own reference surfaces (§45) and removes
// the last of the cross-department rows (§49).
//
// "ALL REQUESTS" IS NOT A PERMISSION (§46). Part 3's ruling stands untouched: this actor holds the
// assigned-only service-request read tier, so `All Requests` and `My Requests` return the SAME rows
// for them. The row exists because §45 lists it and because the same label means the whole register
// for the admin; the SERVER decides what either one returns, and no label widens anything. That is
// asserted in `adminPanelPart4Workspaces.test.js` and proven again in the browser.
//
// §49's removals: `Legal` and `Sales & Bookings` are gone — other departments' workspaces, reached
// contextually from an authorised request if they are needed at all. `Customers` and `Reallocations`
// stay: a service request is ABOUT a customer, and reallocation is a service this department raises.
export const CUSTOMER_SUPPORT_EMPLOYEE_NAV = [
  { section: null, items: [D.dashboard] },
  // PART 2 — the same Requests group as the Admin's, led by the employee's own request worklist (CS6 Phase 6 R8 kept).
  CS_REQUESTS([D.myRequests]),
  // An employee's task view is their own work (BD-4/BD-5): no Team Tasks, no Queue, no approval queues.
  { section: "Customer Support Tasks", items: [D.myTasks] },
  { section: "Customers", items: [D.customers, D.serviceCatalogue] },
  // PHASE 3.1 — the Case register is no longer a cross-department reference.
  { section: "Reference", items: [D.sales] },
  { section: "Records", items: [D.documents] },
  DEPT_REPORTING,
  PERSONAL,
];
export const GENERIC_EMPLOYEE_NAV = employeeProfile({ heading: "My work" });

// ═════════════════════════════════════════════════════════════════════════════
// Profile 4 · ADMINISTRATION staff who are not the Master Admin
// ═════════════════════════════════════════════════════════════════════════════
// Part 1 §1 and §8: Administration has one Master Admin and ZERO operational employees, and the seed
// no longer creates an Administration Department Admin. This profile exists so that such an account
// — if one were ever provisioned by hand — is not handed an operational menu: no department queue,
// no My Tasks, no employee workflow, no operating-department module. It is not a control profile
// either; organisation control belongs to the Master Admin alone.
// Owner decision (2026-10-14): the Administration Department Admin keeps these eight rows and gains its own two
// capabilities, Password Reset and Audit Trail. Both rows carry their existing predicates (`canResetPassword`,
// `canViewAuditTrail`), so a non-admin account that falls to this profile is not shown either. The Master Admin's
// profile (`ADMINISTRATION_NAV`) is separate and untouched.
export const ADMINISTRATION_STAFF_NAV = [
  { section: null, items: [D.dashboard] },
  { section: "Administration", items: [D.passwordReset, D.auditTrail] },
  // A6 — read-only organisation monitoring. Every row carries `canMonitorOrganisation`, so a non-admin account that
  // falls to this profile sees none of it.
  { section: "Monitoring", items: [D.monOverview, D.monDepartments, D.monEmployees, D.monTasks, D.monBottlenecks,
    D.monDeptBoard, D.monEmpBoard, D.monPerformance, D.monActivity] },
  { section: "Personal", items: [D.notifications, D.myActivity] },
  { section: "Reporting", items: REPORTING },
  { section: "Distribution", items: [D.exports] },
];

// ═════════════════════════════════════════════════════════════════════════════
// designation → department → profile
// ═════════════════════════════════════════════════════════════════════════════
const ADMIN_PROFILES = {
  [DEPARTMENTS.PROPERTIES]: { id: "PROPERTIES_NAV", label: DEPARTMENTS.PROPERTIES, sections: PROPERTIES_NAV },
  [DEPARTMENTS.FIELD_OPERATIONS]: { id: "FIELD_OPERATIONS_NAV", label: DEPARTMENTS.FIELD_OPERATIONS, sections: FIELD_OPERATIONS_NAV },
  [DEPARTMENTS.LEGAL]: { id: "LEGAL_NAV", label: DEPARTMENTS.LEGAL, sections: LEGAL_NAV },
  [DEPARTMENTS.REGISTRATION_SALES]: { id: "REGISTRATION_SALES_NAV", label: DEPARTMENTS.REGISTRATION_SALES, sections: REGISTRATION_SALES_NAV },
  [DEPARTMENTS.CUSTOMER_SUPPORT]: { id: "CUSTOMER_SUPPORT_NAV", label: DEPARTMENTS.CUSTOMER_SUPPORT, sections: CUSTOMER_SUPPORT_NAV },
  [DEPARTMENTS.ADMINISTRATION]: { id: "ADMINISTRATION_STAFF_NAV", label: DEPARTMENTS.ADMINISTRATION, sections: ADMINISTRATION_STAFF_NAV },
};

const EMPLOYEE_PROFILES = {
  [DEPARTMENTS.PROPERTIES]: { id: "PROPERTIES_EMPLOYEE_NAV", label: DEPARTMENTS.PROPERTIES, sections: PROPERTIES_EMPLOYEE_NAV },
  [DEPARTMENTS.FIELD_OPERATIONS]: { id: "FIELD_OPERATIONS_EMPLOYEE_NAV", label: DEPARTMENTS.FIELD_OPERATIONS, sections: FIELD_OPERATIONS_EMPLOYEE_NAV },
  [DEPARTMENTS.LEGAL]: { id: "LEGAL_COMMON_NAV", label: DEPARTMENTS.LEGAL, sections: LEGAL_COMMON_NAV },
  [DEPARTMENTS.REGISTRATION_SALES]: { id: "REGISTRATION_SALES_EMPLOYEE_NAV", label: DEPARTMENTS.REGISTRATION_SALES, sections: REGISTRATION_SALES_EMPLOYEE_NAV },
  [DEPARTMENTS.CUSTOMER_SUPPORT]: { id: "CUSTOMER_SUPPORT_EMPLOYEE_NAV", label: DEPARTMENTS.CUSTOMER_SUPPORT, sections: CUSTOMER_SUPPORT_EMPLOYEE_NAV },
  [DEPARTMENTS.ADMINISTRATION]: { id: "ADMINISTRATION_STAFF_NAV", label: DEPARTMENTS.ADMINISTRATION, sections: ADMINISTRATION_STAFF_NAV },
};

const LAWYER_PROFILE = { id: "LAWYER_NAV", label: DEPARTMENTS.LEGAL, sections: LAWYER_NAV };
const GENERIC_ADMIN_PROFILE = { id: "GENERIC_DEPARTMENT_NAV", label: "Department", sections: GENERIC_DEPARTMENT_NAV };
const GENERIC_EMPLOYEE_PROFILE = { id: "GENERIC_EMPLOYEE_NAV", label: "Workspace", sections: GENERIC_EMPLOYEE_NAV };

/**
 * The profile this user's authority calls for: `{ id, label, sections }`.
 * `label` is what the sidebar prints under the brand, so the actor can see at a glance which area
 * they are in — "Master Admin" for the Master Admin, their department for everyone else. (Owner, 2026-10-14: the
 * Master Admin's area was labelled "Administration", the same label the Administration DEPARTMENT's own profile
 * carries, so the two looked identical. Only the visible label changed; the id `ADMINISTRATION_NAV` is kept.)
 * Customers never reach the admin panel.
 */
export function navigationProfileFor(user) {
  if (isMasterAdmin(user)) {
    return { id: "ADMINISTRATION_NAV", label: DESIGNATIONS.MASTER_ADMIN, sections: ADMINISTRATION_NAV };
  }
  if (isDepartmentAdmin(user)) return ADMIN_PROFILES[user?.department] || GENERIC_ADMIN_PROFILE;
  // LEGAL PHASE 2 §10 / PHASE 3 §5 — in Legal, a Lawyer takes the Lawyer's sidebar; a legacy
  // non-Lawyer account falls to LEGAL_COMMON_NAV below (there is no Legal Employee role).
  if (user?.department === DEPARTMENTS.LEGAL && isLawyer(user)) return LAWYER_PROFILE;
  return EMPLOYEE_PROFILES[user?.department] || GENERIC_EMPLOYEE_PROFILE;
}

/** The sections alone — the shape the navigation contracts assert against. */
export function navigationFor(user) {
  return navigationProfileFor(user).sections;
}

/**
 * Apply authority to structure: keep the rows this caller may use, then keep only the sections that
 * still have rows — so a heading never survives its contents.
 *
 * The empty-section guard is DEFENSIVE, not currently load-bearing: because each profile carries
 * only destinations that suit its role, no section goes empty for any real actor today. `projection`
 * is injectable so that guard can still be proven directly rather than left unpinned until some
 * future row makes a section collapse in production.
 */
// CS-5B — `context` carries facts a row's predicate cannot derive from the user alone (today only the departments that
// govern document reusability, read from the catalogue). Every other predicate ignores it.
export function visibleSections(user, projection = navigationFor(user), context = {}) {
  return projection
    .map((group) => ({ ...group, items: group.items.filter((row) => row.can(user, context)) }))
    .filter((group) => group.items.length > 0);
}

/**
 * WHICH ROW IS LIT — and why `NavLink`'s own `isActive` is not enough.
 *
 * `NavLink` matches on PATHNAME and ignores the query string. Administration offers three rows that
 * share `/users` — Employees, Users & Access and Department Admins, distinguished only by their
 * filters — so the default behaviour lit all three at once, which browser testing caught immediately.
 * Reusing an existing route with a filter is exactly what Part 1 §14 asks for, so the MATCHER was
 * fixed rather than the destinations changed.
 *
 * The rule, in order:
 *   • the pathname must match — exactly when the row declares `end`, otherwise on whole segments,
 *     which is what `NavLink` itself does (so `/legal` still lights for `/legal/intake`);
 *   • a row that DECLARES a query string is lit only when the current query is that query;
 *   • a bare-path row YIELDS to a sibling that claims the current query exactly — so `/users` is lit
 *     on `/users` and on `/users/:id`, but steps aside on `/users?userType=staff`.
 *
 * PRE-EXISTING AND LEFT ALONE: a nested destination still lights its parent list, because the parent
 * row declares no `end` — `/verifications/queue` lights "Verifications" too. That predates this part
 * and belongs to those departments' navigation, not to Administration's.
 *
 * This is cosmetic in the strictest sense: it changes nothing about where a link goes or who may
 * follow it.
 */
export function isRowActive(row, location, siblings) {
  const [rowPath, rowQuery = ""] = row.to.split("?");
  const pathMatches = row.end
    ? location.pathname === rowPath
    : location.pathname === rowPath ||
      location.pathname.startsWith(`${rowPath === "/" ? "" : rowPath}/`);
  if (!pathMatches) return false;

  if (rowQuery) return location.search === `?${rowQuery}`;

  // ── A PREFIX MATCH YIELDS TO A MORE SPECIFIC SIBLING ────────────────────────
  // `pathMatches` above accepts a PREFIX, which is what lets `/properties` stay lit on
  // `/properties/:id`. But when another row owns a LONGER path that also matches, the prefix row is
  // not the destination the reader is on — the specific row is. Without this, two rows were active
  // at once and BOTH announced `aria-current="page"`:
  //
  //     /properties/document-requests  ->  `Properties` AND `Documents Requested`
  //     /audit/my-activity             ->  `Audit & Timeline` AND `My Activity`
  //
  // The defect predates this change and was INVISIBLE while both rows sat in one section — the
  // highlight was wrong but the section to open was the same either way. Splitting `Documents
  // Requested` into `Requests` (BD-01) made it observable: `activeCollapsibleSection` returns the
  // FIRST matching section, so the accordion opened `Property work` on a `Requests` route.
  //
  // This is the same "most specific wins" rule React Router applies to the routes themselves, so the
  // sidebar and the router now agree about which destination the reader is at.
  const isPrefixOnly = location.pathname !== rowPath;
  if (
    isPrefixOnly &&
    siblings.some((other) => {
      if (other === row) return false;
      const otherPath = other.to.split("?")[0];
      if (otherPath.length <= rowPath.length) return false;
      return (
        location.pathname === otherPath ||
        location.pathname.startsWith(`${otherPath}/`)
      );
    })
  ) {
    return false;
  }

  return !siblings.some(
    (other) =>
      other !== row &&
      other.to.startsWith(`${rowPath}?`) &&
      `?${other.to.split("?")[1]}` === location.search
  );
}

// ═════════════════════════════════════════════════════════════════════════════
// COLLAPSIBLE SECTION LOGIC — Part 1 (navigation foundation, 2026-09-03)
// ═════════════════════════════════════════════════════════════════════════════
//
// THE MECHANISM ONLY. Not one profile above sets `collapsible: true`, and that is deliberate: which
// rows become dropdown children is an OPEN OWNER DECISION (D-13 demotion, D-15 group naming). Part 1
// builds the capability and stops. With no section flagged, `resolveOpenSection` returns null for
// every profile and the sidebar renders exactly as it did before — which is why every ordered,
// exhaustive navigation contract still passes unmodified.
//
// ── WHY THE LOGIC LIVES HERE AND NOT IN THE COMPONENT ───────────────────────
// `Sidebar.jsx` is presentation and holds no menu of its own (NAV-ARCH pins that). `isRowActive`
// above set the precedent: the decisions are pure functions in this file, so they can be reasoned
// about and tested without a DOM — this repository has no DOM test tooling, and the navigation
// contracts already load this file as source. Adding a stateful accordion to the component would
// have put an untestable decision inside a renderer that is contractually required to make none.
//
// ── A SECTION IS OPTIONALLY COLLAPSIBLE, NEVER IMPLICITLY ───────────────────
// A group gains the behaviour by declaring `collapsible: true` and, optionally, `id`. Every existing
// group omits both, so nothing changes for them. Sections that are not collapsible keep their
// current always-visible heading — Administration's and Registration & Sales' groups are untouched.

/** A stable, DOM-safe id for a section. `id` if declared, else the heading, else the index. */
export function sectionId(group, index) {
  if (group?.id) return String(group.id);
  if (group?.section) {
    return group.section.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-|-$/g, "");
  }
  return `group-${index}`;
}

/** Whether any row in this section is the one the current location lights. */
export function sectionHasActiveRow(group, location, siblings) {
  return (group?.items || []).some((row) => isRowActive(row, location, siblings));
}

/**
 * The id of the collapsible section holding the active row, or null.
 *
 * Only COLLAPSIBLE sections are considered: a flat section is always visible, so "which one would we
 * have to open" is not a question that applies to it.
 */
export function activeCollapsibleSection(sections, location, siblings) {
  for (let i = 0; i < (sections || []).length; i += 1) {
    const group = sections[i];
    if (!group?.collapsible) continue;
    if (sectionHasActiveRow(group, location, siblings)) return sectionId(group, i);
  }
  return null;
}

/**
 * WHICH SECTION IS OPEN — the single source of truth (§6 one-at-a-time, §7 auto-expansion).
 *
 * THE ROUTE WINS. If the current location lights a row inside a collapsible section, that section is
 * open — always, and whatever the reader last clicked. The reader's choice decides only when the
 * route forces nothing, which is every page that does not live inside a collapsible group.
 *
 * ── WHY THE ROUTE WINS, AND NOT THE READER ──────────────────────────────────
 * §7 is unconditional: a child that is active must have its parent open when reached by normal
 * navigation, by clicking a sidebar child, by deep link, by refresh, and by browser Back AND Forward.
 * An earlier draft let the reader's click win while they stayed on one page, keyed on the location it
 * was made at. BROWSER TESTING BROKE IT: open a group by hand on /documents, navigate away, then
 * press Forward back to /documents, and the stale choice resurrected — the group holding the active
 * child stayed shut, which is exactly the failure §7 names. Keying on `location.key` has the same
 * hole, because Back/Forward restore an entry's original key.
 *
 * Precedence removes the failure mode rather than patching it: there is no stored location to go
 * stale, so no sequence of Back/Forward can produce a closed parent for an active child.
 *
 * The cost, stated plainly: on a page inside a collapsible group, that group cannot be collapsed —
 * it reopens, because it is where the reader currently is. That is what "active child can force its
 * parent open" (§31) means. Everywhere else the accordion is fully under the reader's control.
 *
 *   userOpenId === null        nothing open (the initial state, and after closing one)
 *   userOpenId === "<id>"      the reader opened that one
 */
//
// ── P5 (MDS P5-D3, owner 2026-09-21) · WHEN EVERY SECTION IS A DROPDOWN ─────────────────────────
// The rule above was written when a department had one or two collapsible groups. Part 5 makes EVERY
// section a dropdown, and "the route always wins" then meant a reader on any page could never open a
// different section — browser testing (P5-BROWSER-01) found the sidebar unusable except from the
// Dashboard. For sections declared `readerMayOpenOthers`, the route still OPENS its section on
// arrival (navigation, deep link, refresh, Back and Forward — §7 holds), but a choice the reader makes
// AFTER arriving is honoured until the next navigation. The Sidebar passes that choice as
// `userOpenId` only when it was made during the CURRENT navigation (`undefined` otherwise), so no
// stored choice can resurrect on Back/Forward — the failure the rule above exists to prevent.
// Profiles without the flag (the deferred Master Admin's among them) behave exactly as before.
export function resolveOpenSection({ sections, location, siblings, userOpenId }) {
  const forced = activeCollapsibleSection(sections, location, siblings);
  if (forced) {
    const forcedGroup = (sections || []).find((g, i) => sectionId(g, i) === forced);
    if (forcedGroup && forcedGroup.readerMayOpenOthers && userOpenId !== undefined) return userOpenId;
    return forced;
  }
  return userOpenId === undefined ? null : userOpenId;
}

/** Accordion toggle: opening a section closes any other; re-clicking the open one closes it. */
export function toggleOpenSection(current, id) {
  return current === id ? null : id;
}
